import { createClient, createHttpNetworkIntelligenceResolver, PollSignalingClient } from '../packages/zr-call/src/index';
import { webPlatform, mountCallUI } from '../adapters/web/src/index';



// ---------------------------------------------------------------------------
// 1. Synthetic Web Audio Chime Generator
// ---------------------------------------------------------------------------
class DynamicChimeGenerator {
  constructor() {
    this.ctx = null;
    this.ringInterval = null;
  }

  init() {
    if (!this.ctx) {
      this.ctx = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (this.ctx.state === 'suspended') {
      this.ctx.resume();
    }
  }

  playRing() {
    this.init();
    if (this.ringInterval) return;

    const playTone = () => {
      if (!this.ctx) return;
      
      const now = this.ctx.currentTime;
      const osc1 = this.ctx.createOscillator();
      const osc2 = this.ctx.createOscillator();
      const gain = this.ctx.createGain();

      // modulated ring sound
      osc1.frequency.setValueAtTime(440, now); // US ringback tone lower
      osc2.frequency.setValueAtTime(480, now); // US ringback tone upper
      
      osc1.connect(gain);
      osc2.connect(gain);
      gain.connect(this.ctx.destination);

      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.12, now + 0.1);
      gain.gain.setValueAtTime(0.12, now + 1.8);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 2.0);

      osc1.start(now);
      osc2.start(now);
      
      osc1.stop(now + 2.0);
      osc2.stop(now + 2.0);
    };

    playTone();
    this.ringInterval = setInterval(playTone, 3000);
  }

  stopRing() {
    if (this.ringInterval) {
      clearInterval(this.ringInterval);
      this.ringInterval = null;
    }
  }

  playSuccess() {
    this.init();
    this.stopRing();

    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    
    osc.type = 'sine';
    osc.connect(gain);
    gain.connect(this.ctx.destination);

    const now = this.ctx.currentTime;
    osc.frequency.setValueAtTime(523.25, now); // C5
    osc.frequency.setValueAtTime(659.25, now + 0.12); // E5
    
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.15, now + 0.05);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.5);

    osc.start(now);
    osc.stop(now + 0.5);
  }

  playEnd() {
    this.init();
    this.stopRing();

    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    
    osc.type = 'sine';
    osc.connect(gain);
    gain.connect(this.ctx.destination);

    const now = this.ctx.currentTime;
    osc.frequency.setValueAtTime(392.00, now); // G4
    osc.frequency.setValueAtTime(261.63, now + 0.12); // C4
    
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.15, now + 0.05);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.5);

    osc.start(now);
    osc.stop(now + 0.5);
  }
}

const chimes = new DynamicChimeGenerator();

// ---------------------------------------------------------------------------
// 2. Real-time Audio Activity Wave Visualizer
// ---------------------------------------------------------------------------
// Browsers hard-cap AudioContexts at ~6. With up to 10 tiles, one context PER
// visualizer would throw and silently kill later peers' meters — so all visualizers
// share a single context and only create a per-stream source + analyser.
let sharedVizCtx = null;
function getVizCtx() {
  if (!sharedVizCtx) {
    sharedVizCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  if (sharedVizCtx.state === 'suspended') {
    sharedVizCtx.resume();
  }
  return sharedVizCtx;
}

// One shared animation loop drives EVERY tile's meter. Previously each visualizer ran its own
// requestAnimationFrame at 60fps, so a 10-tile grid meant 10 concurrent 60fps loops (~600
// callbacks/sec). A single ~30fps loop iterating the active set holds the meters visually
// identical at a fraction of the main-thread cost, and stops itself when no tiles are active.
const activeVisualizers = new Set();
let vizRafId = null;
let vizLastFrame = 0;
const VIZ_FRAME_MS = 33; // ~30fps

function vizLoop(now) {
  vizRafId = requestAnimationFrame(vizLoop);
  if (now - vizLastFrame < VIZ_FRAME_MS) return;
  vizLastFrame = now;
  for (const viz of activeVisualizers) viz.render();
}

function ensureVizLoop() {
  if (vizRafId == null) vizRafId = requestAnimationFrame(vizLoop);
}

function releaseVizLoop() {
  if (activeVisualizers.size === 0 && vizRafId != null) {
    cancelAnimationFrame(vizRafId);
    vizRafId = null;
  }
}

class AudioActivityVisualizer {
  constructor(stream, containerId) {
    this.stream = stream;
    this.container = document.getElementById(containerId);
    this.audioContext = null;
    this.analyser = null;
    this.dataArray = null;
    this.bars = null;
  }

  // Called once per shared-loop frame; pulls this stream's spectrum and updates its bars.
  render() {
    if (!this.analyser || !this.bars) return;
    this.analyser.getByteFrequencyData(this.dataArray);
    for (let i = 0; i < this.bars.length; i++) {
      const val = this.dataArray[i * 2] || 0;
      const height = Math.max(4, Math.floor((val / 255) * 16));
      this.bars[i].style.height = `${height}px`;
    }
  }

  start() {
    const audioTracks = this.stream.getAudioTracks();
    if (audioTracks.length === 0) return;

    try {
      this.audioContext = getVizCtx();
      this.source = this.audioContext.createMediaStreamSource(this.stream);
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 32;

      this.source.connect(this.analyser);
      this.dataArray = new Uint8Array(this.analyser.frequencyBinCount);
      this.bars = this.container.querySelectorAll('.bar');

      activeVisualizers.add(this);
      ensureVizLoop();
    } catch (e) {
      console.warn('Audio visualizer init skipped:', e);
    }
  }

  stop() {
    activeVisualizers.delete(this);
    releaseVizLoop();
    // Disconnect this stream's source but DO NOT close the shared context —
    // other tiles' visualizers depend on it.
    if (this.source) {
      try { this.source.disconnect(); } catch (e) { /* already disconnected */ }
      this.source = null;
    }
    this.audioContext = null;
    this.analyser = null;
    this.bars = null;
  }
}

// ---------------------------------------------------------------------------
// 3. UI and Logging Handlers
// ---------------------------------------------------------------------------
const consoleLog = (msg, level = 'info') => {
  const consoleEl = document.getElementById('logConsole');
  const line = document.createElement('div');
  line.className = `log-line ${level}`;
  line.innerText = `[${new Date().toLocaleTimeString()}] ${msg}`;
  consoleEl.appendChild(line);
  // Cap the diagnostics log so a long session can't grow the DOM unbounded.
  while (consoleEl.childElementCount > 500) consoleEl.removeChild(consoleEl.firstChild);
  consoleEl.scrollTop = consoleEl.scrollHeight;
};

document.getElementById('btnClearLogs').onclick = () => {
  document.getElementById('logConsole').innerHTML = '';
};

// Escape untrusted text before interpolating into innerHTML. Presence rows carry peer-supplied
// ids/room names from the cross-room presence poll, so they must never be treated as markup.
const escapeHtml = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

// Auto-fill random operator ID if empty
const randomId = 'operator-' + Math.floor(Math.random() * 900 + 100);
document.getElementById('inputSelfId').value = randomId;
document.getElementById('inputRemoteId').value = 'callee-' + Math.floor(Math.random() * 900 + 100);

// ---------------------------------------------------------------------------
// 4. SDK Connection & Call flow logic
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// 4. SDK Connection & Call flow logic
// ---------------------------------------------------------------------------
let client = null;
let clientEventsUnsubscribe = null;
let lastInitParams = null;
let lastRtcPeerToken = null;
let localStream = null;
let localVisualizer = null;
const remoteVideos = new Map();
const remoteVisualizers = new Map();
let micEnabled = true;
let camEnabled = true;
let screenSharing = false;
const peerHealth = new Map(); // peerId -> { level, details } live connection health
const SESSION_TOKEN_KEY = 'auth-token';
const SESSION_USER_KEY = 'auth-user';
const SESSION_ROLE_KEY = 'auth-role';
const RTC_PEER_TOKEN_KEY = 'rtc-peer-token';
const RTC_PEER_ID_KEY = 'rtc-peer-id';
const RTC_PEER_EXPIRES_AT_KEY = 'rtc-peer-expires-at';
let rtcPeerTokenPromise = null;
let rtcPeerTokenPromisePeerId = null;
let currentCallReason = '';
const LEVEL_RANK = { excellent: 4, good: 3, fair: 2, poor: 1, connecting: 0 };
const LEVEL_TEXT = { excellent: 'Excellent', good: 'Healthy', fair: 'Unstable', poor: 'Poor Signal', connecting: 'Connecting...' };

const SCENARIO_DEFS = {
  'p2p-call': {
    id: 'p2p-call',
    title: 'Secure P2P Call Lab',
    capsule: 'P2P',
    summary: 'Configure two peers, prove presence, and place a secure internet call over raw WebRTC + local CoTURN.',
    hintTitle: 'Raw P2P internet calling',
    hintBody: 'This profile is optimized for a WhatsApp-style secure call over the matchmaking network and CoTURN.',
    providerBadge: 'Serverless MatchMaker',
    securityBadge: 'DTLS + peer identity',
    joinLabel: 'Join P2P Call',
    startLabel: 'Start Secure Call',
    connectText: 'Negotiating direct ICE routes and TURN fallback…',
    activeLog: 'Secure raw-P2P WebRTC session established successfully.',
  },
  'p2p-mesh': {
    id: 'p2p-mesh',
    title: 'P2P Mesh Room Lab',
    capsule: 'Mesh',
    summary: 'Exercise the serverless matchmaker, live peer directory, and a small-room full-mesh call up to ten participants.',
    hintTitle: 'Presence-led mesh room',
    hintBody: 'This profile keeps the room on raw P2P and local CoTURN. Use it to validate lobby discovery, direct dialing, and mesh resilience.',
    providerBadge: 'Serverless MatchMaker',
    securityBadge: 'DTLS + peer identity',
    joinLabel: 'Join Mesh Room',
    startLabel: 'Call Selected Peer',
    connectText: 'Connecting mesh peers over raw WebRTC…',
    activeLog: 'P2P mesh session established successfully.',
  },
  conference: {
    id: 'conference',
    title: 'Managed Conference Lab',
    capsule: 'Managed',
    summary: 'Validate Google Meet-style room joins over LiveKit room WebSocket with manager-minted room tokens and media-infra readiness.',
    hintTitle: 'Managed room conferencing',
    hintBody: 'This profile uses the LiveKit room WebSocket for call/group rooms. WHIP/WHEP endpoints remain broadcast-only and are shown for readiness.',
    providerBadge: 'LiveKit Room',
    securityBadge: 'Media token + DTLS',
    joinLabel: 'Join Conference',
    startLabel: 'Call into Room',
    connectText: 'Joining the managed LiveKit room…',
    activeLog: 'Managed conference session established successfully.',
  },
};

const getSessionToken = () => localStorage.getItem(SESSION_TOKEN_KEY) || '';
const getRtcPeerToken = () => localStorage.getItem(RTC_PEER_TOKEN_KEY) || '';

const withBearer = (token, headers = {}) => (
  token ? { ...headers, Authorization: `Bearer ${token}` } : { ...headers }
);

const clearRtcPeerToken = () => {
  localStorage.removeItem(RTC_PEER_TOKEN_KEY);
  localStorage.removeItem(RTC_PEER_ID_KEY);
  localStorage.removeItem(RTC_PEER_EXPIRES_AT_KEY);
  localTurnProbe = null;
};

const clearOperatorAuthState = () => {
  localStorage.removeItem(SESSION_TOKEN_KEY);
  localStorage.removeItem(SESSION_USER_KEY);
  localStorage.removeItem(SESSION_ROLE_KEY);
  clearRtcPeerToken();
  lastInitParams = null;
  lastRtcPeerToken = null;
  rtcPeerTokenPromise = null;
  rtcPeerTokenPromisePeerId = null;
};

const redirectToLogin = (reason = 'auto') => {
  clearOperatorAuthState();
  window.location.replace(`./login.html?logout=${encodeURIComponent(reason)}`);
};

const ensureRtcPeerToken = async (peerId, { forceRefresh = false } = {}) => {
  const sessionToken = getSessionToken();
  if (!sessionToken) {
    redirectToLogin('auto');
    throw new Error('Operator session missing. Sign in again to continue.');
  }

  const cachedToken = getRtcPeerToken();
  const cachedPeerId = localStorage.getItem(RTC_PEER_ID_KEY) || '';
  const cachedExpiresAt = Number(localStorage.getItem(RTC_PEER_EXPIRES_AT_KEY) || '0');
  if (
    !forceRefresh &&
    cachedToken &&
    cachedPeerId === peerId &&
    Number.isFinite(cachedExpiresAt) &&
    cachedExpiresAt - Date.now() > 60_000
  ) {
    return cachedToken;
  }

  if (!forceRefresh && rtcPeerTokenPromise && rtcPeerTokenPromisePeerId === peerId) {
    return rtcPeerTokenPromise;
  }

  const pendingPeerId = peerId;
  const pendingPromise = (async () => {
    const res = await fetch('/api/auth/rtc-token', {
      method: 'POST',
      headers: withBearer(sessionToken, { 'Content-Type': 'application/json' }),
      body: JSON.stringify({ peerId })
    });

    let data = {};
    try {
      data = await res.json();
    } catch (e) {
      data = {};
    }

    if (!res.ok) {
      clearRtcPeerToken();
      const message = data.error || `RTC token mint failed with status ${res.status}`;
      if (res.status === 401) {
        redirectToLogin('auto');
      }
      throw new Error(message);
    }

    if (!data.token) {
      throw new Error('RTC token mint succeeded but returned no token.');
    }

    localStorage.setItem(RTC_PEER_TOKEN_KEY, data.token);
    localStorage.setItem(RTC_PEER_ID_KEY, data.peerId || peerId);
    if (typeof data.expiresAt === 'number') {
      localStorage.setItem(RTC_PEER_EXPIRES_AT_KEY, String(data.expiresAt));
    } else {
      localStorage.removeItem(RTC_PEER_EXPIRES_AT_KEY);
    }
    return data.token;
  })();

  rtcPeerTokenPromisePeerId = pendingPeerId;
  rtcPeerTokenPromise = pendingPromise;
  try {
    return await pendingPromise;
  } finally {
    if (rtcPeerTokenPromise === pendingPromise) {
      rtcPeerTokenPromise = null;
      rtcPeerTokenPromisePeerId = null;
    }
  }
};

const ensureMediaToken = async (roomName, identity, { managerBaseUrl = '', role = 'publisher' } = {}) => {
  const sessionToken = getSessionToken();
  if (!sessionToken) {
    redirectToLogin('auto');
    throw new Error('Operator session missing. Sign in again to continue.');
  }

  const res = await fetch('/api/auth/media-token', {
    method: 'POST',
    headers: withBearer(sessionToken, { 'Content-Type': 'application/json' }),
    body: JSON.stringify({ roomName, identity, role, managerBaseUrl }),
  });

  let data = {};
  try {
    data = await res.json();
  } catch (e) {
    data = {};
  }

  if (!res.ok) {
    const message = data.error || `Media token mint failed with status ${res.status}`;
    if (res.status === 401) {
      redirectToLogin('auto');
    }
    throw new Error(message);
  }

  if (!data.token) {
    throw new Error('Media token mint succeeded but returned no token.');
  }

  return data;
};

// Google Meet off-screen optimization: when a remote tile scrolls out of view (or onto
// a second page), pause its <video> element AND tell the SDK to disable the received
// video track, so a large grid stops burning decode CPU on tiles nobody is watching.
const offscreenVideoObserver =
  typeof IntersectionObserver !== 'undefined'
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const videoEl = entry.target;
            const pid = videoEl.dataset.participantId;
            if (!pid) continue;
            const visible = entry.isIntersecting;
            if (visible) {
              client?.setParticipantVideoActive(pid, true);
              videoEl.play?.().catch(() => {});
            } else {
              client?.setParticipantVideoActive(pid, false);
              videoEl.pause?.();
            }
          }
        },
        { threshold: 0.1 },
      )
    : null;

const selectSignalingType = document.getElementById('selectSignalingType');
const mediaKitUrlContainer = document.getElementById('mediaKitUrlContainer');
const livekitUrlContainer = document.getElementById('livekitUrlContainer');
const mediaWhipEndpointContainer = document.getElementById('mediaWhipEndpointContainer');
const mediaWhepEndpointContainer = document.getElementById('mediaWhepEndpointContainer');
const selectMaxParticipants = document.getElementById('selectMaxParticipants');
const bridgeHarnessLink = document.getElementById('bridgeHarnessLink');
const scenarioButtons = Array.from(document.querySelectorAll('.scenario-card'));

const selectedMaxParticipants = () => parseInt(selectMaxParticipants.value, 10) || 2;

const getScenarioDef = () => {
  const provider = selectSignalingType.value;
  const max = selectedMaxParticipants();
  if (provider === 'whip-whep') return SCENARIO_DEFS.conference;
  if (max <= 2) return SCENARIO_DEFS['p2p-call'];
  return SCENARIO_DEFS['p2p-mesh'];
};

const detectBridgeTargets = () => {
  const targets = [];
  if (window.ReactNativeWebView && typeof window.ReactNativeWebView.postMessage === 'function') targets.push('ReactNativeWebView');
  if (window.webkit?.messageHandlers?.EdgeBlasterRTC) targets.push('iOS:EdgeBlasterRTC');
  if (window.webkit?.messageHandlers?.ZeroRateRTC) targets.push('iOS:ZeroRateRTC');
  if (window.EdgeBlasterRTCAndroid && typeof window.EdgeBlasterRTCAndroid.postMessage === 'function') targets.push('Android:EdgeBlasterRTC');
  if (window.ZeroRateRTCAndroid && typeof window.ZeroRateRTCAndroid.postMessage === 'function') targets.push('Android:ZeroRateRTC');
  if (window.EdgeBlasterRTCFlutter && typeof window.EdgeBlasterRTCFlutter.postMessage === 'function') targets.push('Flutter:EdgeBlasterRTC');
  if (window.ZeroRateRTCFlutter && typeof window.ZeroRateRTCFlutter.postMessage === 'function') targets.push('Flutter:ZeroRateRTC');
  return targets;
};

const setStatusChip = (id, label, state = 'neutral') => {
  const el = document.getElementById(id);
  if (!el) return;
  el.innerText = label;
  el.dataset.state = state;
};

const setReadiness = (prefix, state, label, detail) => {
  const row = document.getElementById(prefix);
  if (row) row.dataset.state = state;
  setStatusChip(`${prefix}State`, label, state);
  const detailEl = document.getElementById(`${prefix}Detail`);
  if (detailEl) detailEl.innerText = detail;
};

const buildBridgeHarnessHref = () => {
  const params = new URLSearchParams();
  const roomId = document.getElementById('inputRoomId')?.value.trim();
  const selfId = document.getElementById('inputSelfId')?.value.trim();
  const authBaseUrl = document.getElementById('inputAuthBaseUrl')?.value.trim();
  const publicAppKey = document.getElementById('inputPublicAppKey')?.value.trim();
  const region = document.getElementById('inputRegionCode')?.value.trim();
  const livekitUrl = document.getElementById('inputLivekitUrl')?.value.trim();
  const whipEndpoint = document.getElementById('inputWhipEndpoint')?.value.trim();
  const whepEndpoint = document.getElementById('inputWhepEndpoint')?.value.trim();
  if (roomId) params.set('roomId', roomId);
  if (selfId) params.set('selfId', selfId);
  if (authBaseUrl) params.set('authBaseUrl', authBaseUrl);
  if (publicAppKey) params.set('publicAppKey', publicAppKey);
  if (region) params.set('region', region);
  if (selectSignalingType.value) params.set('provider', selectSignalingType.value);
  if (selectedMaxParticipants()) params.set('maxParticipants', String(selectedMaxParticipants()));
  if (livekitUrl) params.set('livekitUrl', livekitUrl);
  if (whipEndpoint) params.set('whipEndpoint', whipEndpoint);
  if (whepEndpoint) params.set('whepEndpoint', whepEndpoint);
  const qs = params.toString();
  return `./webrtc.html${qs ? `?${qs}` : ''}`;
};

const renderBridgeTargets = () => {
  const targets = detectBridgeTargets();
  const label = targets.length ? `${targets.length} target${targets.length > 1 ? 's' : ''}` : 'Browser only';
  const detail = targets.length ? targets.join(' | ') : 'No WebView bridge targets attached to this page.';
  const channelList = document.getElementById('bridgeChannelList');
  const runtimeBadge = document.getElementById('bridgeRuntimeBadge');
  const sessionBridgeBadge = document.getElementById('sessionBridgeBadge');
  if (channelList) channelList.innerText = detail;
  if (runtimeBadge) runtimeBadge.innerText = label;
  if (sessionBridgeBadge) sessionBridgeBadge.innerText = targets.length ? 'Native bridge attached' : 'Web SPA';
  setReadiness(
    'readinessBridge',
    targets.length ? 'good' : 'neutral',
    targets.length ? 'Attached' : 'Browser',
    targets.length ? `Detected bridge targets: ${targets.join(', ')}` : 'No native host detected. Use the bridge harness or embed this page inside your WebView host.',
  );
  if (bridgeHarnessLink) bridgeHarnessLink.href = buildBridgeHarnessHref();
};

const renderScenarioRail = () => {
  const scenario = getScenarioDef();
  scenarioButtons.forEach((btn) => {
    btn.dataset.active = String(btn.dataset.scenario === scenario.id);
  });

  const roomId = document.getElementById('inputRoomId')?.value.trim() || 'room-enterprise-1';
  const remoteCount = remoteVideos.size;
  const livePeerCount = 1 + remoteCount;

  const titleEl = document.getElementById('currentScenarioName');
  const summaryEl = document.getElementById('currentScenarioSummary');
  const modeHintTitle = document.getElementById('modeHintTitle');
  const modeHintBody = document.getElementById('modeHintBody');
  if (titleEl) titleEl.innerText = scenario.title;
  if (summaryEl) summaryEl.innerText = scenario.summary;
  if (modeHintTitle) modeHintTitle.innerText = scenario.hintTitle;
  if (modeHintBody) modeHintBody.innerText = scenario.hintBody;

  setStatusChip('currentScenarioCapsule', scenario.capsule, 'neutral');
  setStatusChip('sessionProviderBadge', scenario.providerBadge, selectSignalingType.value === 'whip-whep' ? 'good' : 'neutral');

  const security = client?.e2eeActive
    ? 'E2EE active'
    : selectSignalingType.value === 'whip-whep'
      ? 'Media token + DTLS'
      : 'DTLS + peer identity';
  setStatusChip('sessionSecurityBadge', security, client?.e2eeActive ? 'good' : 'neutral');

  const sessionRoomBadge = document.getElementById('sessionRoomBadge');
  const sessionPeerBadge = document.getElementById('sessionPeerBadge');
  if (sessionRoomBadge) sessionRoomBadge.innerText = roomId;
  if (sessionPeerBadge) sessionPeerBadge.innerText = `${livePeerCount} live`;

  const startBtn = document.getElementById('btnStartCall');
  const joinBtn = document.getElementById('btnJoinRoom');
  if (startBtn) startBtn.innerText = scenario.startLabel;
  if (joinBtn) joinBtn.innerText = scenario.joinLabel;
};

const renderPreflight = () => {
  const sessionToken = getSessionToken();
  const selfId = document.getElementById('inputSelfId')?.value.trim() || '';
  const roomId = document.getElementById('inputRoomId')?.value.trim() || '';
  const authBaseUrl = document.getElementById('inputAuthBaseUrl')?.value.trim() || '';
  const publicAppKey = document.getElementById('inputPublicAppKey')?.value.trim() || '';
  const managerBaseUrl = document.getElementById('inputMediaKitUrl')?.value.trim() || '';
  const livekitUrl = document.getElementById('inputLivekitUrl')?.value.trim() || '';
  const whipEndpoint = document.getElementById('inputWhipEndpoint')?.value.trim() || '';
  const whepEndpoint = document.getElementById('inputWhepEndpoint')?.value.trim() || '';
  const provider = selectSignalingType.value;

  setReadiness(
    'readinessAuth',
    sessionToken ? 'good' : 'bad',
    sessionToken ? 'Ready' : 'Missing',
    sessionToken ? 'Operator session present. The demo can mint peer-scoped RTC tokens.' : 'Operator session missing. Re-authenticate before starting calls.',
  );

  setReadiness(
    'readinessPresence',
    selfId && roomId ? 'good' : 'warn',
    selfId && roomId ? 'Armed' : 'Needs IDs',
    selfId && roomId ? `Participant "${selfId}" can register presence into room "${roomId}".` : 'Set both Room ID and Participant ID so the directory can discover you.',
  );

  if (client?.turnLiveness) {
    const live = client.turnLiveness.alive && client.turnLiveness.turnReachable;
    setReadiness(
      'readinessTurn',
      live ? 'good' : 'warn',
      live ? 'Live' : 'Degraded',
      live
        ? `TURN reachable${client.turnLiveness.expiresAt ? `, credentials expire ${client.turnLiveness.expiresAt}` : ''}.`
        : 'TURN is reachable but degraded, or credentials need rotation. The engine will attempt recovery.',
    );
  } else {
    const hasTurnBootstrap = !!authBaseUrl && !!publicAppKey;
    setReadiness(
      'readinessTurn',
      hasTurnBootstrap ? 'neutral' : 'warn',
      hasTurnBootstrap ? 'Bootstrap set' : 'Needs setup',
      hasTurnBootstrap
        ? 'Auth base URL and public app key are present for dynamic CoTURN credential resolution.'
        : 'Add auth base URL + public app key to verify local CoTURN ICE discovery and TURN liveness.',
    );
  }

  if (provider === 'whip-whep') {
    const hasConferenceBootstrap = !!managerBaseUrl || !!livekitUrl;
    const hasBroadcastEndpoints = !!whipEndpoint || !!whepEndpoint;
    setReadiness(
      'readinessMedia',
      hasConferenceBootstrap ? 'good' : 'warn',
      hasConferenceBootstrap ? 'Managed room ready' : 'Needs LiveKit',
      hasConferenceBootstrap
        ? `Managed room bootstrap configured${hasBroadcastEndpoints ? '; WHIP/WHEP broadcast endpoints are also present.' : '; add WHIP/WHEP only when you need broadcast.'}`
        : 'Provide a manager base URL or a direct LiveKit room WebSocket URL for managed-room testing.',
    );
  } else {
    setReadiness(
      'readinessMedia',
      'neutral',
      'Optional',
      'Managed media infra is not required for the raw P2P path. Switch to the managed-room preset when you need LiveKit + WHIP/WHEP.',
    );
  }
};

const renderSessionTransport = () => {
  if (!peerHealth.size) {
    setStatusChip('sessionTransportBadge', currentCallReason || 'Awaiting ICE', 'neutral');
    return;
  }

  let worst = 'excellent';
  let anyConnecting = false;
  let usingTurn = false;
  let usingTcp = false;
  peerHealth.forEach(({ level, transport, iceState }) => {
    const connected = iceState === 'connected' || iceState === 'completed';
    if (!connected) anyConnecting = true;
    else if ((LEVEL_RANK[level] || 0) < (LEVEL_RANK[worst] || 0)) worst = level;
    if (transport?.usingTurn) usingTurn = true;
    if (transport?.usingTcp) usingTcp = true;
  });

  const label = anyConnecting && worst === 'excellent'
    ? 'Negotiating'
    : usingTurn
      ? `TURN ${usingTcp ? 'TCP' : 'relay'} · ${LEVEL_TEXT[worst]}`
      : usingTcp
        ? `Direct TCP · ${LEVEL_TEXT[worst]}`
        : `Direct UDP · ${LEVEL_TEXT[worst]}`;
  const state = anyConnecting ? 'neutral' : worst === 'poor' ? 'bad' : worst === 'fair' ? 'warn' : 'good';
  setStatusChip('sessionTransportBadge', label, state);
};

const syncExperienceUI = () => {
  syncProviderModeUI();
  renderScenarioRail();
  renderPreflight();
  renderBridgeTargets();
  renderSessionTransport();
};

// The provider selector is the authoritative mode switch and maps 1:1 to the SDK's
// `provider`. It also constrains the participant cap so the two controls can never
// contradict each other:
//   • Serverless MatchMaker (P2P + Polling) → raw WebRTC P2P + local CoTURN ICE, ≤10
//   • Localised WebRTC Media Infra + SFU    → LiveKit room WS for call/group, 11–200
const syncProviderModeUI = () => {
  const provider = selectSignalingType.value; // 'serverless-matchmaker' | 'whip-whep'
  const isSfu = provider === 'whip-whep';

  // Manager bootstrap + room/broadcast endpoints only matter for the SFU mode.
  mediaKitUrlContainer.style.display = isSfu ? 'flex' : 'none';
  livekitUrlContainer.style.display = isSfu ? 'flex' : 'none';
  mediaWhipEndpointContainer.style.display = isSfu ? 'flex' : 'none';
  mediaWhepEndpointContainer.style.display = isSfu ? 'flex' : 'none';

  // Constrain the participant cap to the chosen mode: ≤10 for the mesh, 11–200 for SFU.
  for (const opt of selectMaxParticipants.options) {
    const n = parseInt(opt.value, 10);
    opt.disabled = isSfu ? n <= 10 : n > 10;
  }
  if (isSfu && parseInt(selectMaxParticipants.value, 10) <= 10) {
    selectMaxParticipants.value = '50';
  } else if (!isSfu) {
    if (parseInt(selectMaxParticipants.value, 10) > 10) {
      selectMaxParticipants.value = '2';
    }
  }
};

// Product plan → pins the provider + reveals the peer-role selector for Connectra+.
const syncBillingModeUI = () => {
  const mode = document.getElementById('selectBillingMode')?.value || 'connectra_basic';
  const roleBox = document.getElementById('peerRoleContainer');
  if (mode === 'connectra_plus') {
    if (roleBox) roleBox.style.display = '';
    selectSignalingType.value = 'whip-whep'; // Connectra+ pins the media manager
  } else {
    if (roleBox) roleBox.style.display = 'none';
    selectSignalingType.value = 'serverless-matchmaker'; // Basic pins Raw P2P
  }
  syncExperienceUI();
  refreshWebSnippetIfActive?.();
};
document.getElementById('selectBillingMode')?.addEventListener('change', () => { saveConfig(); syncBillingModeUI(); });
document.getElementById('selectPeerRole')?.addEventListener('change', () => { saveConfig(); refreshWebSnippetIfActive?.(); });

selectSignalingType.onchange = syncExperienceUI;
selectMaxParticipants.onchange = syncExperienceUI;
scenarioButtons.forEach((btn) => {
  btn.onclick = () => {
    const scenario = btn.dataset.scenario;
    if (scenario === 'conference') {
      selectSignalingType.value = 'whip-whep';
      selectMaxParticipants.value = '50';
    } else if (scenario === 'p2p-mesh') {
      selectSignalingType.value = 'serverless-matchmaker';
      selectMaxParticipants.value = '10';
    } else {
      selectSignalingType.value = 'serverless-matchmaker';
      selectMaxParticipants.value = '2';
    }
    saveConfig();
    syncExperienceUI();
  };
});
syncExperienceUI();

const updateParticipantCount = () => {
  const pill = document.getElementById('participantsCountPill');
  const txt = document.getElementById('participantsCountText');
  if (!pill || !txt) return;
  
  if (!client || !localStream) {
    pill.style.display = 'none';
    return;
  }
  
  const count = 1 + remoteVideos.size;
  const selectedMax = parseInt(selectMaxParticipants.value, 10) || 10;
  txt.innerText = `${count}/${selectedMax} Participants`;
  pill.style.display = 'flex';
};

const updateGridLayoutClass = () => {
  const videoGrid = document.getElementById('videoGrid');
  const children = Array.from(videoGrid.children);
  const count = children.length;
  videoGrid.classList.remove('cols-1', 'cols-2', 'cols-3', 'cols-4', 'cols-more');
  if (count <= 1) {
    videoGrid.classList.add('cols-1');
  } else if (count === 2) {
    videoGrid.classList.add('cols-2');
  } else if (count === 3) {
    videoGrid.classList.add('cols-3');
  } else if (count === 4) {
    videoGrid.classList.add('cols-4');
  } else {
    videoGrid.classList.add('cols-more');
  }

  // Optimize off-screen video elements (anything beyond index 3, which is 5th participant and beyond)
  children.forEach((container, idx) => {
    // Local container is at idx === 0; we do not auto-mute/pause it here
    if (idx === 0) return;
    
    const videoEl = container.querySelector('video');
    if (!videoEl) return;
    
    const stream = videoEl.srcObject;
    if (idx >= 4) {
      // Off-screen: pause HTML5 video element and disable tracks
      videoEl.pause();
      if (stream && stream instanceof MediaStream) {
        stream.getVideoTracks().forEach(track => {
          track.enabled = false;
        });
      }
    } else {
      // On-screen: resume HTML5 video element and enable tracks
      if (stream && stream instanceof MediaStream) {
        stream.getVideoTracks().forEach(track => {
          track.enabled = true;
        });
      }
      videoEl.play().catch(err => {
        // Ignore autoplay or user-action interrupts
      });
    }
  });

  updateParticipantCount();
};

let activeSpeakerOrder = [];

const applyActiveSpeakerLayout = (speakers = []) => {
  activeSpeakerOrder = speakers;
  const localActive = speakers.some((speaker) => speaker.isLocal);
  document.getElementById('localContainer')?.classList.toggle('active-speaker', localActive);

  const rank = new Map(
    speakers
      .filter((speaker) => !speaker.isLocal)
      .map((speaker, index) => [speaker.participantId, index]),
  );
  const videoGrid = document.getElementById('videoGrid');
  const remoteContainers = Array.from(videoGrid.querySelectorAll('.remote-video-container'));
  remoteContainers.forEach((container) => {
    const participantId = container.id.replace('container-', '');
    container.classList.toggle('active-speaker', rank.has(participantId));
  });
  remoteContainers
    .sort((a, b) => {
      const aId = a.id.replace('container-', '');
      const bId = b.id.replace('container-', '');
      const aRank = rank.has(aId) ? rank.get(aId) : Number.MAX_SAFE_INTEGER;
      const bRank = rank.has(bId) ? rank.get(bId) : Number.MAX_SAFE_INTEGER;
      if (aRank !== bRank) return aRank - bRank;
      return aId.localeCompare(bId);
    })
    .forEach((container) => videoGrid.appendChild(container));
};


const handleStateChange = (state) => {
  // Room-level signal kept for logging; the WhatsApp-flow UX is driven by
  // 'call-phase-changed' below (handleCallPhase).
  consoleLog(`Room state transitioned to: ${state}`, 'info');
};

// WhatsApp-style call lifecycle, driven by the SDK's folded call phase
// (Ringing → Connecting → Active → Reconnecting → Ended).
const END_REASON_TEXT = {
  'hangup': 'Call Ended.',
  'remote-hangup': 'The other participant hung up.',
  'declined': 'Call Declined.',
  'cancelled': 'Call Cancelled.',
  'no-answer': 'No Answer.',
  'failed': 'Connection Failed.',
};

const handleCallPhase = (phase, reason) => {
  const overlay = document.getElementById('callOverlay');
  const statusEl = document.getElementById('overlayCallStatus');
  const acceptBtn = document.getElementById('btnAcceptCall');
  const declineBtn = document.getElementById('btnDeclineCall');
  const cancelBtn = document.getElementById('btnCancelCall');
  const scenario = getScenarioDef();
  currentCallReason = '';

  consoleLog(`Call phase: ${phase}${reason ? ` (${reason})` : ''}`, phase === 'ended' ? 'warn' : 'info');

  const incoming = !!client?.incomingCall;
  // Accept/Decline only make sense for an unanswered incoming ring (handled by
  // the 'incoming-call' event); hide them on every other transition.
  if (phase !== 'ringing' || !incoming) {
    acceptBtn.style.display = 'none';
    declineBtn.style.display = 'none';
  }
  // The caller's cancel/end control: visible while the overlay covers the action strip
  // (outgoing ring, connecting, reconnecting) so a call is never un-endable.
  const showCancel = !incoming && (phase === 'ringing' || phase === 'connecting' || phase === 'reconnecting');
  cancelBtn.style.display = showCancel ? 'inline-flex' : 'none';

  switch (phase) {
    case 'ringing':
      overlay.style.display = 'flex';
      statusEl.innerText = incoming ? 'Incoming call…' : 'Ringing…';
      currentCallReason = incoming ? 'Incoming ring' : 'Dialing';
      // The incoming-call handler rings for inbound; here we ring only for outbound.
      if (!incoming) chimes.playRing();
      break;
    case 'connecting':
      overlay.style.display = 'flex';
      statusEl.innerText = scenario.connectText;
      currentCallReason = 'Connecting';
      break;
    case 'active':
      overlay.style.display = 'none';
      chimes.playSuccess();
      currentCallReason = 'Live';
      consoleLog(scenario.activeLog, 'success');
      break;
    case 'reconnecting':
      overlay.style.display = 'flex';
      statusEl.innerText = 'Connection unstable — reconnecting…';
      currentCallReason = 'Reconnecting';
      break;
    case 'ended':
      overlay.style.display = 'flex';
      statusEl.innerText = `${END_REASON_TEXT[reason] || 'Call Ended.'} Enter details to start/join a session.`;
      chimes.playEnd();
      currentCallReason = END_REASON_TEXT[reason] || 'Ended';
      teardownStreams();
      void initClientIfIdle().catch((err) => {
        console.warn('Failed to re-initialize RTC client after call end:', err);
      });
      break;
  }
  renderSessionTransport();
  renderScenarioRail();
};

const teardownStreams = () => {
  chimes.stopRing();
  if (localVisualizer) {
    localVisualizer.stop();
    localVisualizer = null;
  }
  
  // Clean up all remote visualizers
  remoteVisualizers.forEach(v => v.stop());
  remoteVisualizers.clear();
  remoteVideos.clear();
  
  const localVideo = document.getElementById('localVideo');
  if (localVideo.srcObject) {
    localVideo.srcObject.getTracks().forEach(t => t.stop());
    localVideo.srcObject = null;
  }
  localStream = null;
  
  document.getElementById('localLabelName').innerText = 'You (Local)';
  
  // Remove all remote containers from the DOM
  const videoGrid = document.getElementById('videoGrid');
  const remoteContainers = videoGrid.querySelectorAll('.remote-video-container');
  remoteContainers.forEach(c => c.remove());
  activeSpeakerOrder = [];
  document.getElementById('localContainer')?.classList.remove('active-speaker');
  
  updateGridLayoutClass();

  // Hide all call controls (floating strip + overlay actions) on teardown.
  document.getElementById('actionStrip').style.display = 'none';
  ['btnCancelCall', 'btnAcceptCall', 'btnDeclineCall'].forEach((id) => {
    const el = document.getElementById(id);
    if (el) el.style.display = 'none';
  });

  // Reset connection-health indicators
  peerHealth.clear();
  renderConnectionHealth();
  renderScenarioRail();
  renderPreflight();
};

// ---------------------------------------------------------------------------
// Connection health & live network diagnostics (Google Meet + WhatsApp blend)
// ---------------------------------------------------------------------------
const setBars = (id, level) => {
  const el = document.getElementById(id);
  if (el) el.dataset.level = level || '';
};

// Stats events (quality-changed / peer-heartbeat) fire per-peer and can burst several within a
// single frame; each HUD render rebuilds tables via innerHTML. Coalesce them so N events in one
// frame collapse to a SINGLE repaint on the next animation frame instead of N full rebuilds.
let hudRenderQueued = false;
function scheduleHudRender() {
  if (hudRenderQueued) return;
  hudRenderQueued = true;
  requestAnimationFrame(() => {
    hudRenderQueued = false;
    renderConnectionHealth();
    updateHudWebRtcQuality();
    renderScenarioRail();
  });
}

// Repaints the global header pill, the local tile bars, and the per-peer
// live metrics table. The worst active link drives the global/local indicator —
// that is effectively the user's own local-network health.
const renderConnectionHealth = () => {
  const pill = document.getElementById('globalHealthPill');
  const text = document.getElementById('globalHealthText');
  const localLabel = document.getElementById('quality-label-local');

  if (!peerHealth.size) {
    setBars('globalHealthBars', '');
    setBars('health-local', '');
    pill.dataset.level = '';
    text.innerText = 'Idle';
    if (localLabel) {
      localLabel.innerText = '🟢';
      localLabel.title = 'Network Quality: EXCELLENT';
    }
  } else {
    let worst = 'excellent';
    let anyConnecting = false;
    peerHealth.forEach(({ level, iceState }) => {
      const isConnected = iceState === 'connected' || iceState === 'completed';
      if (!isConnected) {
        anyConnecting = true;
      } else {
        if (LEVEL_RANK[level] < LEVEL_RANK[worst]) worst = level;
      }
    });
    if (anyConnecting && worst === 'excellent') {
      worst = 'connecting';
    }
    setBars('globalHealthBars', worst);
    setBars('health-local', worst);
    pill.dataset.level = worst;
    text.innerText = LEVEL_TEXT[worst];
    if (localLabel) {
      const emojiMap = { excellent: '🟢', good: '🟢', fair: '🟡', poor: '🔴', connecting: '⏳' };
      localLabel.innerText = emojiMap[worst] || '🟢';
      localLabel.title = `Network Quality: ${worst.toUpperCase()}`;
    }
  }

  renderSessionTransport();

  const wrap = document.getElementById('netDiagnostics');
  if (!peerHealth.size) {
    wrap.innerHTML = '<div class="net-diag-empty">No active peers — live bandwidth &amp; speed-test metrics appear here during a call.</div>';
    return;
  }
  let html = '';
  peerHealth.forEach(({ level, details, iceState }, peerId) => {
    const isConnected = iceState === 'connected' || iceState === 'completed';
    const downText = isConnected ? `${details.downKbps} kbps` : '--';
    const upText = isConnected ? `${details.upKbps} kbps` : '--';
    const rttText = isConnected ? `${details.rtt} ms` : '--';
    const lossText = isConnected ? `${details.lossRate}%` : '--';
    const jitterText = isConnected ? `${details.jitter} ms` : '--';

    html += `
      <div class="net-diag-row">
        <div class="net-diag-peer"><span class="net-diag-dot" data-level="${level}"></span>${peerId}</div>
        <div class="net-diag-metrics">
          <span>&darr; <b>${downText}</b></span>
          <span>&uarr; <b>${upText}</b></span>
          <span>RTT <b>${rttText}</b></span>
          <span>Loss <b>${lossText}</b></span>
          <span>Jitter <b>${jitterText}</b></span>
        </div>
      </div>`;
  });
  wrap.innerHTML = html;
};

const initClientIfIdle = async () => {
  const roomId = document.getElementById('inputRoomId').value.trim();
  const selfId = document.getElementById('inputSelfId').value.trim();
  const password = document.getElementById('inputRoomPassword').value.trim();
  const provider = selectSignalingType.value; // 'serverless-matchmaker' | 'whip-whep'
  const webhookUrl = document.getElementById('inputWebhookUrl').value.trim();
  const webhookSecret = document.getElementById('inputWebhookSecret').value.trim();
  const scenario = getScenarioDef();

  if (!roomId || !selfId) return;

  // If client is already in an active session, don't interrupt it
  if (client && client.callPhase !== 'idle' && client.callPhase !== 'ended') {
    return;
  }

  const rtcPeerToken = await ensureRtcPeerToken(selfId);
  const currentParams = { roomId, selfId, password, provider, webhookUrl, webhookSecret };
  if (
    client &&
    lastInitParams &&
    JSON.stringify(lastInitParams) === JSON.stringify(currentParams) &&
    lastRtcPeerToken === rtcPeerToken
  ) {
    return;
  }

  if (client) {
    if (clientEventsUnsubscribe) {
      clientEventsUnsubscribe();
      clientEventsUnsubscribe = null;
    }
    await client.leave();
  }

  lastInitParams = currentParams;
  lastRtcPeerToken = rtcPeerToken;

  consoleLog(`Initializing ZeroRate RTC client (${selfId}) for ${scenario.title}.`, 'info');

  const signalingClient = new PollSignalingClient({ roomId, peerId: selfId, password });
  const selectedMax = parseInt(selectMaxParticipants.value, 10) || 10;
  const maxParticipants = provider === 'serverless-matchmaker' ? Math.min(10, selectedMax) : selectedMax;

  const mediaManagerBaseUrl = document.getElementById('inputMediaKitUrl').value.trim() || '';
  const livekitUrlInput = (document.getElementById('inputLivekitUrl').value.trim() || '').replace(/\/$/, '');
  const whipEndpointInput = (document.getElementById('inputWhipEndpoint').value.trim() || '').replace(/\/$/, '');
  const whepEndpointInput = (document.getElementById('inputWhepEndpoint').value.trim() || '').replace(/\/$/, '');
  const authBaseUrl = document.getElementById('inputAuthBaseUrl').value.trim() || window.location.origin;
  let mediaSession = null;
  if (provider === 'whip-whep') {
    consoleLog(`Managed room mode enabled (up to ${maxParticipants} participants).`, 'info');
    if (mediaManagerBaseUrl) {
      try {
        mediaSession = await ensureMediaToken(roomId, selfId, { managerBaseUrl: mediaManagerBaseUrl, role: 'publisher' });
        consoleLog(`Minted dedicated room token via ${mediaSession.managerBaseUrl}.`, 'success');
      } catch (err) {
        consoleLog(`Media Manager bootstrap failed: ${err.message || err}`, 'error');
        throw err;
      }
    } else {
      consoleLog('No Media Manager API URL set — supply a room token and LiveKit room URL directly for managed-room testing.', 'warn');
    }
  } else {
    consoleLog(`Raw P2P mode armed for room ${roomId} over the matchmaking network and local CoTURN.`, 'info');
  }

  const livekitUrl = livekitUrlInput || mediaSession?.livekitUrl || mediaSession?.websocketUrl;
  const whipEndpoint = whipEndpointInput || mediaSession?.whipEndpoint;
  const whepEndpoint = whepEndpointInput || mediaSession?.whepEndpoint;
  if (provider === 'whip-whep' && !mediaSession?.token) {
    consoleLog('No dedicated room token minted — falling back to the signaling token. This is suitable only for local stubs, not production media servers.', 'warn');
  }
  if (provider === 'whip-whep' && !livekitUrl) {
    throw new Error('No LiveKit room WebSocket URL set. Provide inputLivekitUrl or return livekitUrl from /api/auth/media-token.');
  }

  client = createClient({
    signaling: signalingClient,
    platform: webPlatform,
    provider,
    livekitUrl,
    whipEndpoint,
    whepEndpoint,
    selfId,
    token: rtcPeerToken,
    mediaToken: mediaSession?.token,
    telemetryWebhookUrl: webhookUrl || undefined,
    telemetryWebhookSecret: webhookSecret || undefined,
    authBaseUrl,
    publicAppKey: document.getElementById('inputPublicAppKey').value.trim(),
    regionCode: document.getElementById('inputRegionCode').value.trim(),
    serviceCategory: 'coturn',
    ui: { style: document.getElementById('selectCallUx')?.value || 'auto', glass: true },
    // Product plan + role (Connectra / Connectra+). Basic = Raw P2P; Plus = media manager + CXaaS roles.
    billingMode: document.getElementById('selectBillingMode')?.value || 'connectra_basic',
    peerRole: document.getElementById('selectBillingMode')?.value === 'connectra_plus'
      ? (document.getElementById('selectPeerRole')?.value || 'client')
      : undefined,
    // Raw link-capacity probe (fast.com-style) against the session-scoped speed-test endpoint,
    // co-located with the CoTURN edge. Distinct from the passive media throughput.
    linkCapacity: { enabled: true, intervalMs: 30000 },
    deviceContext: {
      requestLocationConsent: true,
      networkResolver: createHttpNetworkIntelligenceResolver({
        // Network intelligence is served by THIS app's own backend (Netlify function), NOT the
        // external CoTURN auth service that `authBaseUrl` points at — using authBaseUrl here 404s.
        url: new URL('/api/auth/network-intelligence', window.location.origin).toString(),
        getToken: getSessionToken,
      }),
    },
    ice: {
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' }
      ]
    }
  });
  syncExperienceUI();

  if (webhookUrl) {
    document.getElementById('hudWebhookStatus').innerHTML = `⏳ Connecting Webhook to <span style="font-size:10px">${webhookUrl}</span>...`;
  } else {
    document.getElementById('hudWebhookStatus').innerText = 'Webhook stream inactive. Enter a webhook URL in the Launch tab.';
  }

  clientEventsUnsubscribe = client.events.on((e) => {
    addBridgeLog(e.type, e);

    switch (e.type) {
      case 'state-changed':
        handleStateChange(e.state);
        break;

      case 'call-phase-changed':
        handleCallPhase(e.phase, e.reason);
        break;

      case 'turn-liveness-changed': {
        if (!e.credentialsValid) {
          consoleLog(`⚠️ TURN credentials expired — engine is re-minting and hot-swapping them...`, 'warn');
        } else if (!e.alive || !e.turnReachable) {
          consoleLog(`⚠️ TURN Server unreachable — engine is restarting ICE to recover the route...`, 'warn');
        } else {
          consoleLog(`✓ TURN Server liveness OK (reachable, credentials valid${e.expiresAt ? `, expires ${e.expiresAt}` : ''}).`, 'success');
        }
        renderPreflight();
        break;
      }

      case 'turn-credentials-expiring': {
        // Proactive key rotation: the engine re-mints BEFORE expiry, so the rollover is
        // seamless (no ICE restart, no media blip) — the smooth-UX path for long calls.
        consoleLog(`♻️ TURN credentials expiring in ~${Math.round(e.inMs / 1000)}s — rotating ahead of time (seamless).`, 'info');
        break;
      }

      case 'turn-credentials-refreshed': {
        consoleLog(`✓ TURN credentials re-minted and applied to live connections — session preserved.`, 'success');
        renderPreflight();
        break;
      }

      case 'incoming-call': {
        consoleLog(`Incoming call from ${e.from}`, 'info');
        const overlay = document.getElementById('callOverlay');
        document.getElementById('overlayPeerName').innerText = e.displayName || e.from;
        document.getElementById('overlayAvatar').innerText = (e.from[0] || '?').toUpperCase();
        document.getElementById('overlayCallStatus').innerText = 'Incoming call…';
        document.getElementById('btnAcceptCall').style.display = 'inline-flex';
        document.getElementById('btnDeclineCall').style.display = 'inline-flex';
        overlay.style.display = 'flex';
        chimes.playRing();
        break;
      }
        
      case 'device-context':
        updateHudDeviceContext(e.context);
        renderPreflight();
        break;
        
      case 'webhook-success':
        updateHudWebhookStatus({ type: 'success', status: e.status, url: e.webhookUrl, event: e.event });
        break;
        
      case 'webhook-failed':
        updateHudWebhookStatus({ type: 'error', error: e.error, url: e.webhookUrl, event: e.event });
        break;

      case 'participant-joined':
        consoleLog(`Remote participant joined lobby: ${e.participant.id}`, 'success');
        if (!document.getElementById(`container-${e.participant.id}`)) {
          const videoGrid = document.getElementById('videoGrid');
          const container = document.createElement('div');
          container.className = 'video-container remote-video-container';
          container.id = `container-${e.participant.id}`;
          
          container.innerHTML = `
            <video id="video-${e.participant.id}" autoplay playsinline></video>
            <div class="video-placeholder" id="placeholder-${e.participant.id}">
              <div class="avatar-placeholder">${e.participant.id[0].toUpperCase()}</div>
              <span id="state-text-${e.participant.id}">${e.participant.id} (Ringing...)</span>
            </div>
            <div class="net-health" id="health-${e.participant.id}" data-level="" title="Connection health">
              <span class="sig-bar"></span><span class="sig-bar"></span><span class="sig-bar"></span><span class="sig-bar"></span>
            </div>
            <div class="video-label">
              <span class="mic-status-icon" id="mic-status-${e.participant.id}">🎤</span>
              <span>${e.participant.id}</span>
              <span id="quality-label-${e.participant.id}" class="quality-label-badge" style="margin-left: 6px; font-size: 11px;" title="Network Quality: IDLE">⚪</span>
            </div>
            <div class="audio-wave-visualizer" id="wave-${e.participant.id}">
              <div class="bar"></div><div class="bar"></div><div class="bar"></div><div class="bar"></div>
            </div>
          `;
          
          videoGrid.appendChild(container);
          updateGridLayoutClass();
          
          const videoEl = document.getElementById(`video-${e.participant.id}`);
          videoEl.dataset.participantId = e.participant.id;
          remoteVideos.set(e.participant.id, videoEl);
          offscreenVideoObserver?.observe(videoEl);
        }
        applyActiveSpeakerLayout(activeSpeakerOrder);
        renderScenarioRail();
        break;

      case 'peer-connection-state-changed': {
        const pId = e.participantId;
        const iceState = e.state;
        consoleLog(`ICE Connection state for ${pId} changed to: ${iceState}`, 'info');
        
        const placeholder = document.getElementById(`placeholder-${pId}`);
        const stateText = document.getElementById(`state-text-${pId}`);
        
        if (stateText) {
          if (iceState === 'checking') {
            stateText.innerText = `${pId} (Connecting...)`;
          } else if (iceState === 'connected' || iceState === 'completed') {
            stateText.innerText = `${pId} (Active)`;
          } else if (iceState === 'disconnected') {
            stateText.innerText = `${pId} (Reconnecting...)`;
          } else if (iceState === 'failed') {
            stateText.innerText = `${pId} (Failed)`;
          }
        }
        
        if (placeholder) {
          if (iceState === 'connected' || iceState === 'completed') {
            placeholder.style.display = 'none';
          } else {
            placeholder.style.display = 'flex';
          }
        }
        break;
      }
        
      case 'track-subscribed':
        consoleLog(`Subscribed to remote track from ${e.participantId}: ${e.track.kind}`, 'info');
        const videoEl = remoteVideos.get(e.participantId);
        const stream = e.track.stream;
        
        if (videoEl && stream) {
          videoEl.srcObject = stream;
          if (remoteVisualizers.has(e.participantId)) {
            remoteVisualizers.get(e.participantId).stop();
          }
          const visualizer = new AudioActivityVisualizer(stream, `wave-${e.participantId}`);
          visualizer.start();
          remoteVisualizers.set(e.participantId, visualizer);
        }
        break;
        
      case 'participant-left':
        consoleLog(`Remote participant left: ${e.participantId}`, 'warn');
        if (remoteVisualizers.has(e.participantId)) {
          remoteVisualizers.get(e.participantId).stop();
          remoteVisualizers.delete(e.participantId);
        }
        const leftVideoEl = remoteVideos.get(e.participantId);
        if (leftVideoEl) {
          offscreenVideoObserver?.unobserve(leftVideoEl);
          if (leftVideoEl.srcObject) {
            leftVideoEl.srcObject.getTracks().forEach(t => t.stop());
            leftVideoEl.srcObject = null;
          }
        }
        remoteVideos.delete(e.participantId);
        const containerEl = document.getElementById(`container-${e.participantId}`);
        if (containerEl) {
          containerEl.remove();
        }
        activeSpeakerOrder = activeSpeakerOrder.filter((speaker) => speaker.participantId !== e.participantId);
        applyActiveSpeakerLayout(activeSpeakerOrder);
        updateGridLayoutClass();
        chimes.playEnd();
        renderScenarioRail();
        break;

      case 'active-speakers-changed':
        applyActiveSpeakerLayout(e.speakers || []);
        renderScenarioRail();
        break;
  
      case 'link-capacity': {
        // Raw link capacity (fast.com-style) — distinct from the media throughput above.
        lastLinkCapacity = e;
        renderLinkCapacity(e);
        consoleLog(`📶 Link capacity (raw): ↓${e.downMbps} Mbps ↑${e.upMbps} Mbps · probe RTT ${e.latencyMs ?? '-'}ms`, 'info');
        break;
      }

      case 'quality-changed': {
        const prev = peerHealth.get(e.participantId) || {};
        peerHealth.set(e.participantId, { ...prev, level: e.level, details: e.details });
        setBars(`health-${e.participantId}`, e.level);
        scheduleHudRender();

        const qLabel = document.getElementById(`quality-label-${e.participantId}`);
        if (qLabel) {
          const emojiMap = { excellent: '🟢', good: '🟢', fair: '🟡', poor: '🔴' };
          qLabel.innerText = emojiMap[e.level] || '⚪';
          qLabel.title = `Network Quality: ${e.level.toUpperCase()}`;
        }

        const isConnected = prev.iceState === 'connected' || prev.iceState === 'completed';
        if (isConnected && (!prev.level || prev.level !== e.level)) {
          const d = e.details;
          const t = prev.transport; // { usingTurn, usingTcp } from the peer-heartbeat
          const path = t?.usingTurn
            ? `relayed via CoTURN${t.usingTcp ? '/TCP' : ''}`
            : t?.usingTcp ? 'direct/TCP' : 'direct P2P (UDP)';
          const snap = `${e.participantId}: ${e.level.toUpperCase()} — ↓${d.downKbps}kbps ↑${d.upKbps}kbps · RTT ${d.rtt}ms · loss ${d.lossRate}% · jitter ${d.jitter}ms · ${path}`;
          // Distinguish a genuinely impaired LINK (loss / starved throughput) from the expected
          // higher latency of a TURN-relayed path where media is still flowing fine. The old
          // build wrongly blamed the "local network" for relay-path RTT.
          const impaired = d.lossRate > 3 || d.jitter > 40 || (d.downKbps > 0 && d.downKbps < 100);
          if (e.level === 'poor' && impaired) {
            consoleLog(`⚠ Weak link — ${snap}. May affect call smoothness.`, 'error');
          } else if (e.level === 'poor' || e.level === 'fair') {
            const why = t?.usingTurn ? 'higher latency is expected on the relay path; media is flowing' : 'transient latency';
            consoleLog(`ℹ Network ${e.level} — ${why}. ${snap}`, 'warn');
          } else {
            consoleLog(`✓ Network healthy — ${snap}`, 'success');
          }
        }
        break;
      }

      case 'peer-heartbeat': {
        const hb = e.heartbeat;
        const prev = peerHealth.get(e.participantId) || {};
        peerHealth.set(e.participantId, {
          ...prev,
          level: hb.quality.level,
          details: hb.quality.details,
          transport: hb.transport,
          iceState: hb.iceConnectionState,
          alive: hb.alive,
          ts: hb.ts,
        });

        const t = hb.transport;
        const stateText = document.getElementById(`state-text-${e.participantId}`);
        if (stateText && t) {
          const path = t.usingTurn ? `TURN${t.usingTcp ? '/TCP' : ''}` : t.usingTcp ? 'TCP' : 'P2P';
          stateText.dataset.transport = path;
        }
        if (t && (!prev.transport || prev.transport.usingTurn !== t.usingTurn || prev.transport.usingTcp !== t.usingTcp)) {
          const path = t.usingTurn ? `relayed via CoTurn TURN${t.usingTcp ? ' over TCP' : ''}` : t.usingTcp ? 'direct over TCP' : 'direct P2P (UDP)';
          consoleLog(`Transport for ${e.participantId}: ${path} (${hb.connectionState}/${hb.iceConnectionState})`, 'info');
        }
        if (!hb.alive && hb.staleTicks >= 2) {
          const state = hb.iceConnectionState;
          const isPolite = selfId < e.participantId;
          const willRestart = ((state === 'disconnected' || state === 'failed') && hb.staleTicks >= 2 && !isPolite) ||
                              ((state === 'new' || state === 'checking') && hb.staleTicks >= 10 && !isPolite);
          consoleLog(`⚠ ${e.participantId} stale for ${hb.staleTicks} keep-alive ticks (state: ${state})${willRestart ? ' — engine is restarting ICE.' : ''}`, 'warn');
        }
        scheduleHudRender();
        break;
      }

      case 'screen-share-changed':
        updateShareButton(e.active);
        if (!e.active && localStream) {
          document.getElementById('localVideo').srcObject = localStream;
        }
        applyCamState();
        break;

      case 'reaction-received':
        floatReaction(e.emoji);
        break;

      case 'hand-raised-changed': {
        const label = document.getElementById(`hand-${e.participantId}`);
        if (label) label.style.display = e.raised ? 'inline' : 'none';
        consoleLog(`✋ ${e.participantId} ${e.raised ? 'raised' : 'lowered'} their hand (${e.raisedHands.length} up)`, 'info');
        break;
      }

      case 'peer-identity':
        // Verifiable public-key identity: surface the safety number so two users can compare
        // it out-of-band and confirm no MITM swapped a key in the signaling relay.
        consoleLog(`🔑 ${e.participantId} identity verified — fingerprint ${e.fingerprint} · safety ${e.safetyNumber}`, 'success');
        setPeerSafety(e.participantId, e.fingerprint, e.safetyNumber);
        break;
    }

    renderPreflight();
  });
};

// Floating emoji reaction overlay (created lazily so it works regardless of markup).
function floatReaction(emoji) {
  let layer = document.getElementById('reactionLayer');
  if (!layer) {
    layer = document.createElement('div');
    layer.id = 'reactionLayer';
    layer.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:9999;overflow:hidden;';
    document.body.appendChild(layer);
  }
  const el = document.createElement('div');
  el.innerText = emoji;
  el.style.cssText = `position:absolute;bottom:120px;left:${20 + Math.random() * 60}%;font-size:42px;transition:all 2.4s ease-out;opacity:1;`;
  layer.appendChild(el);
  requestAnimationFrame(() => { el.style.bottom = '70%'; el.style.opacity = '0'; });
  setTimeout(() => el.remove(), 2500);
}

// Render a peer's verified safety number into the telemetry HUD (created lazily).
function setPeerSafety(participantId, fingerprint, safetyNumber) {
  let host = document.getElementById('peerSafetyList');
  if (!host) {
    const tele = document.getElementById('telemetryQualityList') || document.body;
    host = document.createElement('div');
    host.id = 'peerSafetyList';
    host.style.cssText = 'margin-top:8px;font-family:monospace;font-size:10px;color:#a5b4fc;';
    tele.parentElement ? tele.parentElement.appendChild(host) : tele.appendChild(host);
  }
  let row = document.getElementById(`safety-${participantId}`);
  if (!row) { row = document.createElement('div'); row.id = `safety-${participantId}`; host.appendChild(row); }
  row.innerText = `🔑 ${participantId} · fp ${fingerprint} · safety ${safetyNumber}`;
}

const connectRtcClient = async (isJoinOnly) => {
  const roomId = document.getElementById('inputRoomId').value.trim();
  const selfId = document.getElementById('inputSelfId').value.trim();
  const remoteId = document.getElementById('inputRemoteId').value.trim();
  const password = document.getElementById('inputRoomPassword').value.trim();
  const provider = selectSignalingType.value;
  const selectedMax = parseInt(selectMaxParticipants.value, 10) || 10;
  const maxParticipants = provider === 'serverless-matchmaker' ? Math.min(10, selectedMax) : selectedMax;
  const scenario = getScenarioDef();

  if (!roomId || !selfId) {
    alert('Please enter Room ID and Participant ID');
    return;
  }

  // Ensure background listener is initialized
  await initClientIfIdle();

  try {
    // Acquire local media streams
    consoleLog('Requesting camera & microphone access...', 'info');
    localStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });

    // Hand the SAME stream to the SDK so there is a single capture and the mic/cam
    // toggles below actually mute/blank what remote peers receive.
    client.provideLocalStream(localStream);
    // Re-apply current mute/camera state to the (possibly pre-toggled) stream.
    localStream.getAudioTracks().forEach(t => (t.enabled = micEnabled));
    localStream.getVideoTracks().forEach(t => (t.enabled = camEnabled));

    const localVideo = document.getElementById('localVideo');
    localVideo.srcObject = localStream;
    document.getElementById('localLabelName').innerText = `${selfId} (You)`;
    
    // Start local audio waves
    localVisualizer = new AudioActivityVisualizer(localStream, 'localWave');
    localVisualizer.start();
    updateGridLayoutClass();

    // Trigger Join or Call Initiation
    if (isJoinOnly) {
      consoleLog(`Joining ${scenario.title.toLowerCase()} in room ${roomId}.`, 'info');
      await client.join({ roomId, mode: maxParticipants <= 2 ? 'call' : 'group', password, max: maxParticipants });
    } else {
      consoleLog(`Calling ${remoteId} in room ${roomId} using ${scenario.title}.`, 'info');
      document.getElementById('overlayPeerName').innerText = remoteId;
      document.getElementById('overlayAvatar').innerText = (remoteId[0] || '?').toUpperCase();
      await client.start({ to: remoteId, roomId, password });
    }

    // Activate Call HUD Strip Controls
    document.getElementById('actionStrip').style.display = 'flex';
    renderScenarioRail();
    renderPreflight();
  } catch (err) {
    consoleLog(`Call initialization failed: ${err.message}`, 'error');
    console.error(err);
  }
};

// Attach control buttons
document.getElementById('btnStartCall').onclick = () => connectRtcClient(false);
document.getElementById('btnJoinRoom').onclick = () => connectRtcClient(true);

// Native glassmorphism call-UI kit preview (the same mountCallUI a developer ships). Renders
// the WhatsApp (Secure 1:1) or Google Meet (room/conference) experience over the page, driven
// by the live client + the selected Call Experience.
let nativeCallUi = null;
const btnLaunchCallUi = document.getElementById('btnLaunchCallUi');
const btnCloseCallUi = document.getElementById('btnCloseCallUi');
const closeNativeCallUi = () => {
  if (nativeCallUi) { nativeCallUi.unmount(); nativeCallUi = null; }
  btnCloseCallUi.style.display = 'none';
};
if (btnLaunchCallUi) {
  btnLaunchCallUi.onclick = () => {
    initClientIfIdle();
    if (!client) { consoleLog('Enter a Participant ID + Room ID first, then Launch Native Call UI.', 'warn'); return; }
    closeNativeCallUi();
    const style = document.getElementById('selectCallUx')?.value || 'auto';
    const max = parseInt(document.getElementById('selectMaxParticipants').value, 10) || 2;
    const mode = max <= 2 ? 'call' : 'group';
    nativeCallUi = mountCallUI(client, {
      style,
      mode,
      maxParticipants: max,
      glass: true,
      displayName: document.getElementById('inputRemoteId')?.value.trim() || 'Peer',
    });
    btnCloseCallUi.style.display = '';
    consoleLog(`Launched native ${client.ui.style} call UI (glassmorphism).`, 'success');
  };
}
if (btnCloseCallUi) btnCloseCallUi.onclick = closeNativeCallUi;

// Single, convenient teardown for both ending a started P2P call AND leaving a joined
// lobby. client.hangup() notifies the 1:1 callee (if any) and announces a graceful leave
// to every mesh peer, then tears down — so the action is smooth from any call phase.
const endSession = async () => {
  if (!client) return;
  consoleLog('Ending session — notifying peers and releasing media…', 'warn');
  // Hide the overlay's cancel immediately for snappy feedback; phase 'ended' resets the UI.
  document.getElementById('btnCancelCall').style.display = 'none';
  try {
    await client.hangup();
  } catch (err) {
    consoleLog(`Failed to end session: ${err.message}`, 'error');
  }
};
document.getElementById('btnEndCall').onclick = endSession;
document.getElementById('btnCancelCall').onclick = endSession;

// WhatsApp-style Accept / Decline for an incoming ring.
document.getElementById('btnAcceptCall').onclick = async () => {
  if (!client) return;
  document.getElementById('btnAcceptCall').style.display = 'none';
  document.getElementById('btnDeclineCall').style.display = 'none';
  consoleLog('Accepting incoming call', 'success');
  chimes.stopRing();
  try {
    await client.accept();
    document.getElementById('actionStrip').style.display = 'flex';
  } catch (err) {
    consoleLog(`Failed to accept call: ${err.message}`, 'error');
  }
};

document.getElementById('btnDeclineCall').onclick = async () => {
  if (!client) return;
  document.getElementById('btnAcceptCall').style.display = 'none';
  document.getElementById('btnDeclineCall').style.display = 'none';
  consoleLog('Declining incoming call', 'warn');
  chimes.stopRing();
  await client.decline();
};

// Microphone + camera enabled/disabled state UI
const applyMicState = () => {
  const btn = document.getElementById('btnToggleMic');
  btn.innerText = micEnabled ? '🎤' : '🔇';
  btn.classList.toggle('muted', !micEnabled);
  btn.title = micEnabled ? 'Mute Microphone' : 'Unmute Microphone';
  btn.setAttribute('aria-pressed', String(!micEnabled));
  btn.setAttribute('aria-label', micEnabled ? 'Microphone on, activate to mute' : 'Microphone muted, activate to unmute');
  const tileIcon = document.getElementById('localMicStatus');
  if (tileIcon) tileIcon.classList.toggle('show', !micEnabled);
};

const applyCamState = () => {
  const btn = document.getElementById('btnToggleCam');
  btn.innerText = camEnabled ? '📹' : '🚫';
  btn.classList.toggle('muted', !camEnabled);
  btn.title = camEnabled ? 'Turn Camera Off' : 'Turn Camera On';
  btn.setAttribute('aria-pressed', String(!camEnabled));
  btn.setAttribute('aria-label', camEnabled ? 'Camera on, activate to turn off' : 'Camera off, activate to turn on');
  // While screen sharing, the local preview shows the composite (screen + presenter bubble),
  // so the full-cover "Camera Off" overlay must NOT hide it — turning the camera off
  // only drops the bubble, never the screen share.
  const sharing = !!(client && client.isScreenSharing);
  const camOff = document.getElementById('localCamOff');
  if (camOff) camOff.style.display = (!camEnabled && !sharing) ? 'flex' : 'none';
};

document.getElementById('btnToggleMic').onclick = () => {
  micEnabled = !micEnabled;
  if (localStream) localStream.getAudioTracks().forEach(t => (t.enabled = micEnabled));
  applyMicState();
  consoleLog(`Microphone ${micEnabled ? 'enabled' : 'muted'}`, micEnabled ? 'info' : 'warn');
  if (client) client.setMicEnabled(micEnabled);
};

document.getElementById('btnToggleCam').onclick = () => {
  camEnabled = !camEnabled;
  if (localStream) localStream.getVideoTracks().forEach(t => (t.enabled = camEnabled));
  applyCamState();
  consoleLog(`Camera ${camEnabled ? 'enabled' : 'disabled'}`, camEnabled ? 'info' : 'warn');
  if (client) client.setCameraEnabled(camEnabled);
};

// Initialize control glyphs to the enabled state on load
applyMicState();
applyCamState();

// Screen share is now a baked-in SDK capability: startScreenShare({ selfOverlay })
// composites the presenter's camera as a circular bubble onto the shared screen and
// publishes that single stream to all peers (StreamYard-style). The demo just drives
// the SDK and previews the composite locally.
const updateShareButton = (active) => {
  const btn = document.getElementById('btnShareScreen');
  btn.classList.toggle('active', active);
  btn.setAttribute('aria-pressed', String(active));
  btn.setAttribute('aria-label', active ? 'Stop screen share' : 'Share screen with self-view');
};

document.getElementById('btnShareScreen').onclick = async () => {
  if (!client || !localStream) return;
  try {
    if (!client.isScreenSharing) {
      consoleLog('Starting screen share with circular self-view overlay...', 'info');
      await client.startScreenShare({ selfOverlay: true });
      // Preview the composited stream (screen + presenter bubble) locally.
      const preview = client.displayStream;
      if (preview) document.getElementById('localVideo').srcObject = preview;
      updateShareButton(true);
      applyCamState(); // hide the cam-off overlay so it can't mask the shared screen
      consoleLog('Screen sharing live — your face is overlaid on the shared screen for everyone.', 'success');
    } else {
      await client.stopScreenShare();
      document.getElementById('localVideo').srcObject = localStream;
      updateShareButton(false);
      applyCamState();
      consoleLog('Stopped screen share, camera restored.', 'info');
    }
  } catch (e) {
    consoleLog(`Screen share error: ${e.message}`, 'error');
  }
};

// ---------------------------------------------------------------------------
// 5. Mobile UDM Bridge Controller Demo
// ---------------------------------------------------------------------------
const updateUdmConsole = (type, payload) => {
  const schema = {
    source: 'udm-web',
    type,
    payload,
    timestamp: new Date().toISOString()
  };
  document.getElementById('udmOutput').innerText = JSON.stringify(schema, null, 2);
};

document.getElementById('btnTriggerNativeCam').onclick = () => {
  consoleLog('UDM WebView: Requesting Native Camera stream roll.', 'info');
  if (window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(JSON.stringify({ source: 'udm-web', type: 'rtc-get-usermedia', payload: { audio: false, video: true } }));
  }
  updateUdmConsole('rtc-get-usermedia', { audio: false, video: true });
};

document.getElementById('btnTriggerNativeMic').onclick = () => {
  consoleLog('UDM WebView: Requesting Native Microphone capture channel.', 'info');
  if (window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(JSON.stringify({ source: 'udm-web', type: 'rtc-get-usermedia', payload: { audio: true, video: false } }));
  }
  updateUdmConsole('rtc-get-usermedia', { audio: true, video: false });
};

document.getElementById('btnTriggerNativePush').onclick = () => {
  const remoteId = document.getElementById('inputRemoteId').value.trim();
  const roomId = document.getElementById('inputRoomId').value.trim();
  consoleLog(`UDM WebView: Transmitting call push notification invite to ${remoteId}`, 'info');
  if (window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(JSON.stringify({ source: 'udm-web', type: 'fcm-push-invite', payload: { to: remoteId, roomId } }));
  }
  updateUdmConsole('fcm-push-invite', { to: remoteId, roomId });
};

document.getElementById('btnCopyUdmCode').onclick = () => {
  navigator.clipboard.writeText(document.getElementById('udmOutput').innerText);
  consoleLog('Copied UDM message schema to clipboard.', 'success');
};

// Display default UDM schema for native-to-web device/network attestation injection.
updateUdmConsole('set-device-context', {
  capturedAt: Date.now(),
  fingerprint: {
    userAgent: 'NativeWebView/1.0',
    screenResolution: '1170x2532',
    colorDepth: 24,
    language: 'en-US',
    timezone: 'Africa/Lagos',
    hardwareConcurrency: 8,
    platform: 'ios',
    devices: [],
  },
  location: {
    latitude: 6.5244,
    longitude: 3.3792,
    accuracy: 12,
    timestamp: Date.now(),
    source: 'native',
  },
  network: {
    publicIp: '203.0.113.10',
    country: 'Nigeria',
    region: 'Lagos',
    city: 'Lagos',
    isp: 'Example ISP',
    source: 'native',
    confidence: 'high',
    resolvedAt: Date.now(),
    proxy: {
      status: 'clear',
      source: 'native',
      reason: 'Resolved by the native trust service.',
    },
  },
});

// ---------------------------------------------------------------------------
// 6. Dynamic Theme Toggle
// ---------------------------------------------------------------------------
const themeGroup = document.getElementById('themeGroup');
themeGroup.querySelectorAll('button').forEach(btn => {
  btn.onclick = () => {
    themeGroup.querySelectorAll('button').forEach(b => b.classList.remove('active'));
    btn.classList.add('active');
    
    const theme = btn.getAttribute('data-theme');
    localStorage.setItem('theme-mode', theme);
    
    if (theme === 'system') {
      document.documentElement.removeAttribute('data-theme');
    } else {
      document.documentElement.setAttribute('data-theme', theme);
    }
    consoleLog(`Theme changed to: ${theme}`, 'info');
  };
});

// ---------------------------------------------------------------------------
// 7. Telemetry HUD and Bridge event visualization
// ---------------------------------------------------------------------------
const addBridgeLog = (type, payload) => {
  const bridgeLogs = document.getElementById('hudBridgeLogs');
  if (!bridgeLogs) return;
  const line = document.createElement('div');
  line.className = 'log-line success';
  line.innerText = `[${new Date().toLocaleTimeString()}] Bridge: ${type} - ${JSON.stringify(payload).substring(0, 80)}...`;
  bridgeLogs.appendChild(line);
  bridgeLogs.scrollTop = bridgeLogs.scrollHeight;
};

const updateHudDeviceContext = (context) => {
  window.__lastDeviceContext = context; // cache so the raw-capacity line can re-render live
  const fg = context.fingerprint || {};
  const loc = context.location;
  const net = context.network;
  const att = context.attestation || {};
  const devList = (fg.devices || []).map(d => `- ${d.kind}: ${d.label}`).join('\n') || 'None detected';
  const uaBrands = (fg.userAgentBrands || []).join(', ') || 'Unavailable';
  const connectionTraits = [
    fg.connectionType ? `Type ${fg.connectionType}` : '',
    fg.downlinkMbps ? `Downlink ${fg.downlinkMbps} Mbps` : '',
    fg.networkRttMs ? `RTT ${fg.networkRttMs} ms` : '',
    typeof fg.online === 'boolean' ? (fg.online ? 'Online' : 'Offline') : '',
  ].filter(Boolean).join(' | ') || 'Unavailable';

  document.getElementById('hudFingerprint').innerText =
    `OS Platform: ${fg.platform || 'unknown'}
UA: ${(fg.userAgent || 'unknown').substring(0, 72)}...
Brands: ${uaBrands}
Resolution: ${fg.screenResolution || 'unknown'} (Depth: ${fg.colorDepth || 0})
Language: ${fg.language || 'unknown'}
Timezone: ${fg.timezone || 'unknown'}
Cores: ${fg.hardwareConcurrency || 0} | Memory: ${fg.deviceMemory || 'unknown'} GB
Network Hints: ${connectionTraits}
Devices:\n${devList}`;

  if (loc) {
    const locality = [
      loc.address?.city,
      loc.address?.region,
      loc.address?.country,
    ].filter(Boolean).join(', ');
    document.getElementById('hudLocation').innerHTML =
      `Latitude: <b>${loc.latitude.toFixed(5)}</b>
Longitude: <b>${loc.longitude.toFixed(5)}</b>
Accuracy: <b>${loc.accuracy.toFixed(1)}m</b>
Timestamp: ${new Date(loc.timestamp).toLocaleTimeString()}
Source: ${loc.source || 'browser'}${locality ? `<br>Locality: <b>${locality}</b>` : ''}
<br><br>
<a href="https://www.google.com/maps?q=${loc.latitude},${loc.longitude}" target="_blank" style="color:var(--accent); text-decoration:underline;">View on Maps</a>`;
  } else {
    document.getElementById('hudLocation').innerText = `GPS unavailable: ${context.locationError || 'Unknown error'}`;
  }

  const proxy = net?.proxy || {};
  const networkWarnings = [...(net?.warnings || []), ...(context.networkError ? [context.networkError] : [])];
  document.getElementById('hudNetwork').innerText = net
    ? `Public IP: ${net.publicIp || 'Unavailable'}
Country / City: ${[net.country || net.countryCode, net.region, net.city].filter(Boolean).join(' / ') || 'Unavailable'}
ISP / Org: ${[net.isp, net.organization].filter(Boolean).join(' / ') || 'Unavailable'}
ASN: ${net.asn || 'Unavailable'}
Resolver Source: ${net.source} (${net.confidence} confidence)
Proxy / VPN: ${proxy.status || 'unknown'}${proxy.reason ? ` — ${proxy.reason}` : ''}
Flags: ${['proxy', 'vpn', 'tor', 'hosting', 'relay'].filter((flag) => proxy[flag]).join(', ') || 'none'}
Warnings: ${networkWarnings.join(' | ') || 'None'}`
    : `No trusted network resolver is attached yet.
Expected fields when configured: public IP, country, state/city, ISP, ASN, and proxy/VPN flags.
Last error: ${context.networkError || 'None'}`;

  const transport = att.transport || {};
  const peerSummary = att.peerSummary || {};
  const security = att.security || {};
  const cap = lastLinkCapacity;
  document.getElementById('hudAttestation').innerText =
    `Provider: ${att.provider || selectSignalingType.value}
Room: ${att.roomId || document.getElementById('inputRoomId')?.value.trim() || 'Not joined'}
Raw Link Capacity: ${cap ? `↓ ${cap.downMbps} Mbps · ↑ ${cap.upMbps} Mbps · probe RTT ${cap.latencyMs ?? '-'} ms (fast.com-style, vs media throughput above)` : 'Probing…'}
Path Locality: ${att.locality || 'unknown'}
Primary Path: ${
      transport.usingTurn == null
        ? 'No active peer path yet'
        : transport.usingTurn
          ? `TURN relay${transport.usingTcp ? ' over TCP/TLS' : ''}`
          : transport.usingTcp
            ? 'Direct TCP'
            : 'Direct UDP P2P'
    }
Candidate Pair: ${[transport.localCandidate, transport.remoteCandidate].filter(Boolean).join(' -> ') || 'Unavailable'}
Peers: total ${peerSummary.totalPeers || 0}, active ${peerSummary.activePeers || 0}, direct ${peerSummary.directPeers || 0}, relayed ${peerSummary.relayedPeers || 0}, tcp ${peerSummary.tcpPeers || 0}
TURN Health: ${
      att.turn?.turnReachable == null
        ? 'Unavailable'
        : att.turn.turnReachable
          ? `Reachable${att.turn.credentialsValid === false ? ' / rotating credentials' : ''}`
          : 'Unreachable'
    }${
      turnServiceHealth
        ? ` · Service ${turnServiceHealth.status || '?'} (realm ${turnServiceHealth.realm || '?'}, redis ${turnServiceHealth.redis || '?'}, mongo ${turnServiceHealth.mongo || '?'}${turnServiceHealth.certificateDaysLeft != null ? `, cert ${turnServiceHealth.certificateDaysLeft}d` : ''})`
        : ''
    }
Security: ${security.e2eeActive ? 'E2EE active' : security.e2eeRequested ? 'E2EE requested but not active' : 'DTLS/SRTP transport only'}
Verification: ${security.verification || 'unknown'}${security.reason ? ` — ${security.reason}` : ''}
Warnings: ${(att.warnings || []).join(' | ') || 'None'}`;
};

const updateHudWebhookStatus = (statusInfo) => {
  const el = document.getElementById('hudWebhookStatus');
  if (!el) return;
  if (statusInfo.type === 'success') {
    el.innerHTML = `<span style="color:var(--emerald)">● Active (HTTP ${statusInfo.status})</span><br>
URL: <span style="font-size:10px">${statusInfo.url}</span><br>
Last Post: ${statusInfo.event} (${new Date().toLocaleTimeString()})`;
  } else {
    el.innerHTML = `<span style="color:var(--rose)">● Connection Error</span><br>
URL: <span style="font-size:10px">${statusInfo.url}</span><br>
Error: ${statusInfo.error}`;
  }
};

const updateHudWebRtcQuality = () => {
  const container = document.getElementById('telemetryQualityList');
  if (!container) return;
  if (!peerHealth.size) {
    container.innerHTML = '<div class="telemetry-empty">No active peer connection stats. Initiate a call to begin telemetry streaming.</div>';
    return;
  }

  let html = '';
  peerHealth.forEach(({ level, details, iceState }, peerId) => {
    const isConnected = iceState === 'connected' || iceState === 'completed';
    const rttText = isConnected ? `${details.rtt}ms` : '--';
    const lossText = isConnected ? `${details.lossRate}%` : '--';
    const jitterText = isConnected ? `${details.jitter}ms` : '--';
    const downText = isConnected ? `${details.downKbps} kbps` : '--';
    const upText = isConnected ? `${details.upKbps} kbps` : '--';
    const bweText = isConnected ? `${details.availableOutgoingKbps} kbps` : '--';

    const displayLevel = isConnected ? level.toUpperCase() : 'CONNECTING...';
    const displayColor = isConnected 
      ? (level === 'excellent' || level === 'good' ? 'var(--emerald)' : level === 'fair' ? 'var(--amber)' : 'var(--rose)')
      : '#3b82f6';

    html += `
      <div class="telemetry-peer-card">
        <div class="telemetry-peer-head">
          <b>${peerId}</b>
          <span class="telemetry-peer-status" data-level="${isConnected ? level : 'connecting'}" style="color:${displayColor}">${displayLevel}</span>
        </div>
        <div class="telemetry-peer-metrics">
          <div>Latency RTT: <b>${rttText}</b></div>
          <div>Loss Rate: <b>${lossText}</b></div>
          <div>Jitter: <b>${jitterText}</b></div>
          <div>In Throughput: <b>${downText}</b></div>
          <div>Out Throughput: <b>${upText}</b></div>
          <div>BWE Estimate: <b>${bweText}</b></div>
        </div>
      </div>`;
  });
  container.innerHTML = html;
};

// Local HTTP WAN Speed Probe Loop
let wanPingInterval = null;
const startWanPingLoop = () => {
  if (wanPingInterval) clearInterval(wanPingInterval);
  
  const pingProbe = async () => {
    const tStart = Date.now();
    try {
      const res = await fetch('/api/signaling/participants?roomId=ping-test');
      if (res.ok) {
        const latency = Date.now() - tStart;
        consoleLog(`[Diagnostics] Serverless HTTP WAN Latency: ${latency}ms | Signal Node: Stable`, 'info');
        
        const banner = document.getElementById('connectionBanner');
        if (latency > 350) {
          banner.classList.add('show');
        } else {
          banner.classList.remove('show');
        }
      }
    } catch (e) {
      console.warn('WAN speed test probe failed:', e);
    }
  };
  
  pingProbe();
  wanPingInterval = setInterval(pingProbe, 6000);
};

const stopWanPingLoop = () => {
  if (wanPingInterval) {
    clearInterval(wanPingInterval);
    wanPingInterval = null;
  }
};

// ---------------------------------------------------------------------------
// 8. HUD Tab Panels Switching and Code Generator Content
// ---------------------------------------------------------------------------
document.querySelectorAll('.hud-tab').forEach(btn => {
  btn.onclick = () => {
    document.querySelectorAll('.hud-tab').forEach(b => b.classList.remove('active'));
    btn.classList.add('active');
    
    const tabId = btn.getAttribute('data-tab');
    document.querySelectorAll('.hud-tab-content').forEach(c => c.style.display = 'none');
    document.getElementById(tabId).style.display = 'block';
  };
});

// The web snippet is generated LIVE from the Call Session Controller form so the
// developer copies exactly the configuration they've dialed in — every controller
// parameter is surfaced (see buildWebSnippet below). Mobile/CTV snippets are static
// WebView-bridge boilerplate that load the same UMD bundle.
const SNIPPETS = {

  'ios-swift': `import WebKit
import UIKit

class WebRTCViewController: UIViewController, WKScriptMessageHandler, WKUIDelegate {
    var webView: WKWebView!
    
    override func viewDidLoad() {
        super.viewDidLoad()
        
        let config = WKWebViewConfiguration()
        let controller = WKUserContentController()
        
        // Register message bridges for both namespace options
        controller.add(self, name: "ZeroRateRTC")
        controller.add(self, name: "EdgeBlasterRTC")
        config.userContentController = controller
        
        // Request permissions in config for iOS 15+ media capture
        config.allowsInlineMediaPlayback = true
        config.mediaTypesRequiringUserActionForPlayback = []
        
        webView = WKWebView(frame: self.view.bounds, configuration: config)
        webView.uiDelegate = self
        self.view.addSubview(webView)
        
        // Load the local boilerplate template with UMD bundle
        if let htmlUrl = Bundle.main.url(forResource: "webrtc", withExtension: "html") {
            webView.loadFileURL(htmlUrl, allowingReadAccessTo: htmlUrl.deletingLastPathComponent())
        }
    }
    
    // Intercept telemetry / bridge messages from WebView
    func userContentController(_ userContentController: WKUserContentController, didReceive message: WKScriptMessage) {
        guard let bodyString = message.body as? String,
              let data = bodyString.data(using: .utf8) else { return }
        
        if let json = try? JSONSerialization.jsonObject(with: data, options: []) as? [String: Any] {
            print("Received WebView event: \\(json)")
            // Parse telemetry e.g. quality-changed, device-context
        }
    }
    
    // WKUIDelegate to bypass native prompt alerts inside WebViews
    func webView(_ webView: WKWebView, decidePolicyFor navigationAction: WKNavigationAction, decisionHandler: @escaping (WKNavigationActionPolicy) -> Void) {
        decisionHandler(.allow)
    }
}`,

  'ios-objc': `#import <WebKit/WebKit.h>
#import "WebRTCViewController.h"

@interface WebRTCViewController () <WKScriptMessageHandler, WKUIDelegate>
@property (nonatomic, strong) WKWebView *webView;
@end

@implementation WebRTCViewController

- (void)viewDidLoad {
    [super viewDidLoad];
    
    WKWebViewConfiguration *config = [[WKWebViewConfiguration alloc] init];
    WKUserContentController *controller = [[WKUserContentController alloc] init];
    
    [controller addScriptMessageHandler:self name:@"ZeroRateRTC"];
    [controller addScriptMessageHandler:self name:@"EdgeBlasterRTC"];
    config.userContentController = controller;
    config.allowsInlineMediaPlayback = YES;
    config.mediaTypesRequiringUserActionForPlayback = WKAudiovisualMediaTypeNone;
    
    self.webView = [[WKWebView alloc] initWithFrame:self.view.bounds configuration:config];
    self.webView.UIDelegate = self;
    [self.view addSubview:self.webView];
    
    NSURL *htmlUrl = [[NSBundle mainBundle] URLForResource:@"webrtc" withExtension:@"html"];
    [self.webView loadFileURL:htmlUrl allowingReadAccessToURL:[htmlUrl URLByDeletingLastPathComponent]];
}

- (void)userContentController:(WKUserContentController *)userContentController didReceiveScriptMessage:(WKScriptMessage *)message {
    if ([message.body isKindOfClass:[NSString class]]) {
        NSData *data = [message.body dataUsingEncoding:NSUTF8StringEncoding];
        NSDictionary *json = [NSJSONSerialization JSONObjectWithData:data options:0 error:nil];
        NSLog(@"Received WebView event: %@", json);
    }
}
@end`,

  'android-kt': `import android.os.Bundle
import android.webkit.JavascriptInterface
import android.webkit.PermissionRequest
import android.webkit.WebChromeClient
import android.webkit.WebView
import androidx.appcompat.app.AppCompatActivity
import org.json.JSONObject

class WebRTCActivity : AppCompatActivity() {
    private lateinit var webView: WebView

    override fun onCreate(savedInstanceState: Bundle?) {
        super.onCreate(savedInstanceState)
        webView = WebView(this)
        setContentView(webView)

        webView.settings.javaScriptEnabled = true
        webView.settings.domStorageEnabled = true
        webView.settings.mediaPlaybackRequiresUserGesture = false

        // Add two-way Javascript interfaces for both namespaces
        webView.addJavascriptInterface(WebAppInterface(), "ZeroRateRTCAndroid")
        webView.addJavascriptInterface(WebAppInterface(), "EdgeBlasterRTCAndroid")

        webView.webChromeClient = object : WebChromeClient() {
            // Auto-approve hardware permission demands inside the WebView
            override fun onPermissionRequest(request: PermissionRequest) {
                request.grant(request.resources)
            }
        }

        webView.loadUrl("file:///android_asset/webrtc.html")
    }

    inner class WebAppInterface {
        @JavascriptInterface
        fun postMessage(message: String) {
            val json = JSONObject(message)
            // Handle telemetry event, location data, quality logs, etc.
            android.util.Log.d("WebRTC", "Event from WebView: $json")
        }
    }
}`,

  'android-java': `import android.os.Bundle;
import android.webkit.JavascriptInterface;
import android.webkit.PermissionRequest;
import android.webkit.WebChromeClient;
import android.webkit.WebView;
import androidx.appcompat.app.AppCompatActivity;
import org.json.JSONObject;

public class WebRTCActivity extends AppCompatActivity {
    private WebView webView;

    @Override
    protected void onCreate(Bundle savedInstanceState) {
        super.onCreate(savedInstanceState);
        webView = new WebView(this);
        setContentView(webView);

        webView.getSettings().setJavaScriptEnabled(true);
        webView.getSettings().setDomStorageEnabled(true);
        webView.getSettings().setMediaPlaybackRequiresUserGesture(false);

        WebAppInterface webAppInterface = new WebAppInterface();
        webView.addJavascriptInterface(webAppInterface, "ZeroRateRTCAndroid");
        webView.addJavascriptInterface(webAppInterface, "EdgeBlasterRTCAndroid");

        webView.setWebChromeClient(new WebChromeClient() {
            @Override
            public void onPermissionRequest(PermissionRequest request) {
                request.grant(request.getResources());
            }
        });

        webView.loadUrl("file:///android_asset/webrtc.html");
    }

    public class WebAppInterface {
        @JavascriptInterface
        public void postMessage(String message) {
            try {
                JSONObject json = new JSONObject(message);
                android.util.Log.d("WebRTC", "Event from WebView: " + json.toString());
            } catch (Exception e) {
                e.printStackTrace();
            }
        }
    }
}`,

  'rn-native': `// Native React Native integration — drives the SDK engine directly via
// react-native-webrtc (no WebView). Same zr-call API as web, so reactions, raise-hand,
// video effects, screen share, E2EE and scheduling all work natively.
//
//   npm i zr-call react-native-webrtc
//   # iOS: add NSCameraUsageDescription / NSMicrophoneUsageDescription; pod install
//   # Android: CAMERA + RECORD_AUDIO permissions
import React, { useEffect, useRef, useState } from 'react';
import { SafeAreaView, View, Pressable, Text, StyleSheet } from 'react-native';
import {
  RTCPeerConnection, RTCView, mediaDevices, registerGlobals,
} from 'react-native-webrtc';
import { createClient, PollSignalingClient } from 'zr-call';

registerGlobals(); // wires RN-WebRTC globals (RTCPeerConnection, MediaStream, etc.)

// Map the SDK PlatformAdapter onto react-native-webrtc.
const rnPlatform = {
  createPeerConnection: (cfg) => new RTCPeerConnection({ iceServers: cfg.iceServers }),
  getUserMedia: (c) => mediaDevices.getUserMedia(c),
  getDisplayMedia: () => mediaDevices.getDisplayMedia(),
  createMediaStream: (tracks) => new MediaStream(tracks || []),
};

export default function CallScreen() {
  const clientRef = useRef(null);
  const [localUrl, setLocalUrl] = useState(null);
  const [remoteUrl, setRemoteUrl] = useState(null);
  const [hands, setHands] = useState([]);

  useEffect(() => {
    (async () => {
      const client = createClient({
        signaling: new PollSignalingClient({ roomId: 'support-lobby', peerId: 'mobile-user', password: '' }),
        platform: rnPlatform,
        selfId: 'mobile-user',
        token: 'RTC_PEER_TOKEN', // mint a peer-scoped token server-side or via /api/auth/rtc-token
        authBaseUrl: 'https://api.zerorate.africa',
        publicAppKey: 'pk_live_...',
        regionCode: 'NG',
        provider: 'serverless-matchmaker',
        // ── Product selection (native-to-web parity with the web SDK) ────────────
        // 'connectra_basic' → raw P2P VoIP, WhatsApp-style, ≤10 (this snippet).
        // 'connectra_plus'  → CXaaS contact centre: set provider:'whip-whep' and add
        //   peerRole: 'client' | 'operator' | 'admin', availability: 'active'.
        billingMode: 'connectra_basic',
        e2ee: { secret: 'SHARED_ROOM_SECRET' }, // end-to-end encrypted P2P
        // Call experience the RN UI should mirror: 'whatsapp' (1:1) | 'google-meet' (room) | 'auto'.
        // 'client.ui.style' resolves it; render the matching native controls with react-native-webrtc.
        ui: { style: 'auto', glass: true },
        ice: { iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] },
      });
      clientRef.current = client;
      // console.log('Render', client.ui.style, 'controls'); // 'whatsapp' → PiP+round bar, 'google-meet' → grid+rail

      client.events.on((e) => {
        if (e.type === 'track-subscribed' && e.track.kind === 'video') setRemoteUrl(e.track.stream.toURL());
        if (e.type === 'hand-raised-changed') setHands(e.raisedHands);
        if (e.type === 'reaction-received') { /* animate e.emoji */ }
        if (e.type === 'incoming-call') { /* e.silent → drop in without ringing */ }
        if (e.type === 'quality-changed') { /* LIVE: e.details.downKbps/upKbps/rtt/lossRate/jitter */ }
        if (e.type === 'peer-heartbeat') { /* e.heartbeat.transport.usingTurn/usingTcp — path */ }
        if (e.type === 'peer-identity') { /* verify e.safetyNumber out-of-band (anti-MITM) */ }
      });

      const local = await mediaDevices.getUserMedia({ audio: true, video: true });
      setLocalUrl(local.toURL());
      client.provideLocalStream(local);
      await client.join({ roomId: 'support-lobby', mode: 'group', max: 10 });
    })();
    return () => { clientRef.current?.leave(); };
  }, []);

  const react = () => clientRef.current?.sendReaction('🎉');
  const raise = () => clientRef.current?.raiseHand(!clientRef.current?.isHandRaised);
  const blur  = () => clientRef.current?.setVideoEffect('blur');
  const share = () => clientRef.current?.startScreenShare({ selfOverlay: true });

  return (
    <SafeAreaView style={styles.c}>
      {remoteUrl && <RTCView streamURL={remoteUrl} style={styles.remote} objectFit="cover" />}
      {localUrl && <RTCView streamURL={localUrl} style={styles.local} objectFit="cover" zOrder={1} />}
      <Text style={styles.hands}>{hands.map((h, i) => '✋ ' + (i + 1) + '. ' + h.peerId).join('  ')}</Text>
      <View style={styles.bar}>
        <Pressable onPress={react}><Text style={styles.btn}>👍</Text></Pressable>
        <Pressable onPress={raise}><Text style={styles.btn}>✋</Text></Pressable>
        <Pressable onPress={blur}><Text style={styles.btn}>✨</Text></Pressable>
        <Pressable onPress={share}><Text style={styles.btn}>🖥️</Text></Pressable>
        <Pressable onPress={() => clientRef.current?.leave()}><Text style={styles.btn}>🛑</Text></Pressable>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  c: { flex: 1, backgroundColor: '#090d16' },
  remote: { flex: 1 },
  local: { position: 'absolute', top: 16, right: 16, width: 96, height: 140, borderRadius: 12 },
  hands: { position: 'absolute', top: 16, left: 16, color: '#fde047', fontFamily: 'monospace' },
  bar: { flexDirection: 'row', justifyContent: 'space-around', padding: 16, backgroundColor: '#03050c' },
  btn: { fontSize: 26 },
});`,

  rn: `import React, { useRef } from 'react';
import { StyleSheet, SafeAreaView, Alert } from 'react-native';
import { WebView } from 'react-native-webview';

export default function WebRTCScreen() {
  const webViewRef = useRef(null);

  const handleMessage = (event) => {
    try {
      const json = JSON.parse(event.nativeEvent.data);
      console.log('Received from WebView:', json);
      // process quality stats, device context, etc.
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <WebView
        ref={webViewRef}
        source={{ uri: 'file:///android_asset/webrtc.html' }} // Android
        // source={require('./webrtc.html')} // iOS
        originWhitelist={['*']}
        javaScriptEnabled={true}
        domStorageEnabled={true}
        mediaPlaybackRequiresUserGesture={false}
        allowsInlineMediaPlayback={true}
        onMessage={handleMessage}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#090d16' }
});`,

  flutter: `import 'dart:convert';
import 'package:flutter/material.dart';
import 'package:webview_flutter/webview_flutter.dart';
import 'package:permission_handler/permission_handler.dart';

class WebRTCScreen extends StatefulWidget {
  const WebRTCScreen({Key? key}) : super(key: key);
  @override
  State<WebRTCScreen> createState() => _WebRTCScreenState();
}

class _WebRTCScreenState extends State<WebRTCScreen> {
  late final WebViewController _controller;

  @override
  void initState() {
    super.initState();
    _requestPermissions();

    _controller = WebViewController()
      ..setJavaScriptMode(JavaScriptMode.unrestricted)
      ..setBackgroundColor(const Color(0x00000000))
      ..addJavaScriptChannel(
        'ZeroRateRTCFlutter',
        onMessageReceived: (JavaScriptMessage message) {
          final json = jsonDecode(message.message);
          print("Telemetry Event: $json");
        },
      )
      ..addJavaScriptChannel(
        'EdgeBlasterRTCFlutter',
        onMessageReceived: (JavaScriptMessage message) {
          final json = jsonDecode(message.message);
          print("Telemetry Event: $json");
        },
      )
      ..loadRequest(Uri.parse('asset://assets/webrtc.html'));
  }

  Future<void> _requestPermissions() async {
    await [Permission.camera, Permission.microphone, Permission.location].request();
  }

  @override
  Widget build(BuildContext context) {
    return Scaffold(
      body: SafeArea(
        child: WebViewWidget(controller: _controller),
      ),
    );
  }
}`
};

const codeOutput = document.getElementById('codegenSnippetOutput');
const PROVIDER_LABEL = {
  'serverless-matchmaker': 'Serverless MatchMaker (P2P + Polling)',
  'whip-whep': 'Managed LiveKit Room + WHIP/WHEP Media Infra',
};

// Build the web integration snippet from the LIVE Call Session Controller form, so every
// parameter the SDK accepts is reflected for the developer's exact configuration.
const buildWebSnippet = () => {
  const val = (id, fb) => (document.getElementById(id)?.value.trim() || fb);
  const provider = selectSignalingType.value; // 'serverless-matchmaker' | 'whip-whep'
  const isSfu = provider === 'whip-whep';
  const selectedMax = parseInt(selectMaxParticipants.value, 10) || 10;
  const maxParticipants = isSfu ? selectedMax : Math.min(10, selectedMax);
  const roomId = val('inputRoomId', 'support-lobby');
  const selfId = val('inputSelfId', 'operator-alpha');
  const password = val('inputRoomPassword', '');
  const publicAppKey = val('inputPublicAppKey', 'pk_live_...');
  const regionCode = val('inputRegionCode', 'NG');
  const managerBaseUrl = val('inputMediaKitUrl', 'https://edge-devs.soa.africa/api/webrtc/v1/docs/index.html');
  const livekitUrl = val('inputLivekitUrl', 'wss://media.example.com');
  const whipEndpoint = val('inputWhipEndpoint', 'https://media.example.com/whip');
  const whepEndpoint = val('inputWhepEndpoint', 'https://media.example.com/whep');
  const webhookUrl = val('inputWebhookUrl', '');
  const mode = maxParticipants <= 2 ? 'call' : 'group';
  const callUx = document.getElementById('selectCallUx')?.value || 'auto';
  const billingMode = document.getElementById('selectBillingMode')?.value || (isSfu ? 'connectra_plus' : 'connectra_basic');
  const isPlus = billingMode === 'connectra_plus';
  const peerRole = document.getElementById('selectPeerRole')?.value || 'client';
  const productLines = isPlus
    ? `\n  // ── ZeroRate Connectra+ (CXaaS digital contact centre) ─────────────────────────────\n` +
      `  // Full WebRTC Media Manager + WHIP/WHEP. Peers take a role in the room's operator\n` +
      `  // consumer-group / client publisher model (authoritative queue is the edge Redis Stream,\n` +
      `  // name = room_id, at ${managerBaseUrl}).\n` +
      `  billingMode: '${billingMode}',\n` +
      `  peerRole: '${peerRole}',   // 'client' (publisher) | 'operator' (consumer) | 'admin' (supervisor/escalation)\n` +
      `  availability: 'active',    // operators/admins advertise {active,busy,offline} to the queue\n`
    : `\n  // ── ZeroRate Connectra (raw P2P VoIP, WhatsApp-style, ≤10) ─────────────────────────\n` +
      `  billingMode: '${billingMode}',\n`;

  const sfuLines = isSfu
    ? `\n  // Manager bootstrap stays server-side; the browser receives only a short-lived media token.\n` +
      `  livekitUrl: mediaSession.livekitUrl || mediaSession.websocketUrl || '${livekitUrl}',\n` +
      `  mediaToken: mediaSession.token,\n` +
      `  // Broadcast-only endpoints (optional unless you use mode: 'broadcast').\n` +
      `  whipEndpoint: mediaSession.whipEndpoint || '${whipEndpoint}',\n` +
      `  whepEndpoint: mediaSession.whepEndpoint || '${whepEndpoint}',`
    : '';
  const webhookLines = webhookUrl ? `\n  telemetryWebhookUrl: '${webhookUrl}',\n  telemetryWebhookSecret: 'WEBHOOK_SECRET_KEY',` : '';
  const mediaBootstrapFn = isSfu
    ? `
async function mintMediaToken(roomName, identity) {
  const res = await fetch('/api/auth/media-token', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: \`Bearer \${sessionToken}\`,
    },
    body: JSON.stringify({
      roomName,
      identity,
      role: 'publisher',
      managerBaseUrl: '${managerBaseUrl}',
    }),
  });
  if (!res.ok) throw new Error(\`Media token mint failed (\${res.status})\`);
  return res.json();
}
`
    : '';

  return `import { createClient, createHttpNetworkIntelligenceResolver, PollSignalingClient } from 'zr-call';
import { webPlatform, mountCallUI } from '@zr/adapter-web';

const sessionToken = localStorage.getItem('auth-token') || 'OPERATOR_SESSION_TOKEN';

async function mintRtcPeerToken(peerId) {
  const res = await fetch('/api/auth/rtc-token', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: \`Bearer \${sessionToken}\`,
    },
    body: JSON.stringify({ peerId }),
  });
  if (!res.ok) throw new Error(\`RTC peer token mint failed (\${res.status})\`);
  const data = await res.json();
  return data.token;
}
${mediaBootstrapFn}

// Mode: ${PROVIDER_LABEL[provider]} (≤${maxParticipants} participants)
const selfId = '${selfId}';
const rtcPeerToken = await mintRtcPeerToken(selfId);
${isSfu ? `const mediaSession = await mintMediaToken('${roomId}', selfId);\n` : ''}const client = createClient({
  signaling: new PollSignalingClient({ roomId: '${roomId}', peerId: selfId, password: '${password}' }),
  platform: webPlatform,
  selfId,
  token: rtcPeerToken,

  // Signaling Infrastructure Provider — the authoritative engine mode:
  provider: '${provider}',
${productLines}
  // Native call-UI experience (rendered by mountCallUI below). 'auto' → 1:1 = WhatsApp,
  // room/conference = Google Meet. glass = frosted glassmorphism styling.
  ui: { style: '${callUx}', glass: true },

  // Raw link-capacity probe (fast.com-style) — a real download/upload speed test against a
  // session-scoped endpoint co-located with the CoTURN edge. Emits 'link-capacity' with down/up
  // Mbps + latency, distinct from the passive media throughput in 'quality-changed'.
  linkCapacity: { enabled: true, intervalMs: 30000 },

  // Region-aware dynamic CoTURN credentials. Resolution chain (auto, at join()):
  //   GET  {authBaseUrl}/api/authsvc/v1/region/server-address?region-code=${regionCode}&service=coturn
  //        (header  x-header-key: <publicAppKey>)            → { data: { server_address } }
  //   POST {server_address}/v1/auth/token   { public_app_key, subject, ttl_seconds }   → { token, expires_at }
  //   POST {server_address}/v1/credentials  (Bearer token) { identity, ttl_seconds }    → { username, password, uris, expires }
  authBaseUrl: '${val('inputAuthBaseUrl', window.location.origin)}',
  publicAppKey: '${publicAppKey}',
  regionCode: '${regionCode}',
  serviceCategory: 'coturn',
  deviceContext: {
    requestLocationConsent: true,
    // Point this at YOUR OWN backend that implements /network-intelligence (it detects the
    // client's public IP server-side and enriches it with GeoIP/ISP/proxy). It is NOT the
    // external CoTURN auth service — using that host returns 404.
    networkResolver: createHttpNetworkIntelligenceResolver({
      url: new URL('/api/auth/network-intelligence', window.location.origin).toString(),
      getToken: () => sessionToken,
    }),
  },
${sfuLines}
  // Keep-alive probes CoTURN /v1/peers/ping every 3–5s for live liveness + transport path,
  // and PROACTIVELY rotates the TURN credentials before they expire (seamless, no media blip):
  keepAliveMs: 4000,
  ringTimeoutMs: 45000, // outgoing-call ring timeout before 'no-answer'
${isSfu ? '' : `
  // End-to-end encryption (P2P path): the relay only ever sees ciphertext. Both peers must
  // share the same out-of-band secret. Needs Insertable Streams (Chrome/Edge); 'client.e2eeActive'
  // reports whether it actually engaged.
  e2ee: { secret: 'SHARED_ROOM_SECRET' },`}
  // ICE transport policy: 'all' (default) tries direct P2P over UDP first and relays only when
  // needed — the path that yields UDP most often. The engine also merges a STUN entry from the
  // TURN host and orders servers UDP-first (STUN → TURN/UDP → TURN/TCP → TURNS/TLS) so a healthy
  // link doesn't needlessly run over the slow TCP/TLS relay. Set 'relay' to force TURN.
  iceTransportPolicy: 'all',
  ice: {
    // Static STUN (kept + merged with the dynamic TURN creds, not discarded):
    iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
  },${webhookLines}
});

client.events.on((event) => {
  switch (event.type) {
    case 'call-phase-changed':       /* Ringing → Connecting → Active → Ended */ break;
    case 'incoming-call':            /* event.silent → drop in without ringing */ break;
    case 'device-context':           /* browser + GPS + resolver-backed IP / ISP / proxy + live ICE/TURN/E2EE attestation */ break;
    case 'quality-changed':          /* media throughput: event.details.{downKbps,upKbps,rtt,lossRate,jitter,availableOutgoingKbps} */ break;
    case 'link-capacity':            /* RAW link capacity (fast.com-style): event.{downMbps,upMbps,latencyMs} */ break;
    case 'peer-heartbeat':           /* per-peer state + TURN/TCP transport path + quality */ break;
    case 'peer-identity':            /* verified peer: { fingerprint, safetyNumber } — compare out-of-band */ break;
    case 'reaction-received':        /* { from, emoji } — render a floating reaction */ break;
    case 'hand-raised-changed':      /* { participantId, raised, raisedHands[] } */ break;
    case 'turn-liveness-changed':    /* CoTURN reachability (live /v1/peers/ping); SDK auto-reconnects */ break;
    case 'turn-credentials-expiring': /* proactive rotation: re-minting BEFORE expiry (seamless) */ break;
    case 'turn-credentials-refreshed': /* fresh creds hot-swapped onto live connections */ break;
    case 'presence-changed':         /* Online Presence Directory updated: event.peers[] each with
                                        { peerId, online_status, e2ee_status, turns_status,
                                          peerRole?, availability_status? } — render the directory */ break;
  }
});
${isPlus ? `
// ── Connectra+ Online Presence Directory & contact-centre queue ────────────────
// The SDK maintains a native presence directory for everyone in this room_id (+ room_password).
// Feed it from your presence poll / edge stream; it emits 'presence-changed' (above).
// client.ingestPresence(peers);                 // peers: [{ peerId, online_status, turns_status, peerRole, availability_status }]
// console.log(client.onlinePeers);              // ordered: online + available operators first
// client.setAvailability('busy');               // operator/admin advertises to the queue
${peerRole === 'client'
  ? '// A client publishes to the room stream; if all operators are busy you are placed in the call_queue,\n// and may request a call_me_back slot within business hours (scheduled + served by the edge backend).'
  : peerRole === 'operator'
  ? '// An operator is a consumer-group member: it pulls the next waiting client from the queue and\n// serves scheduled call_me_back requests.'
  : '// An admin supervises operators (working hours, call rates, dropped calls, online state) and can be\n// escalated INTO an active operator call.'}
` : ''}

await client.join({ roomId: '${roomId}', mode: '${mode}', max: ${maxParticipants}${password ? `, password: '${password}'` : ''} });

// ── Native call UI (glassmorphism) — WhatsApp (1:1) / Google Meet (room) ────────
// One line renders the familiar controls + tiles/PiP, wired to this client. 'client.ui'
// resolves 'auto' → the right experience for the room. Works in-browser AND in a WebView
// (web-to-native). Returns a handle: ui.unmount(), ui.setStyle('whatsapp'|'google-meet').
const ui = mountCallUI(client, client.ui);

// ── In-call features (WhatsApp + Google Meet UX) ───────────────────────────────
client.sendReaction('🎉');                 // emoji reaction (rate-limited per sender)
client.raiseHand(true);                     // raise/lower hand; see client.raisedHands
await client.setVideoEffect('blur');        // camera filter/background: blur|grayscale|sepia|vivid|cool|warm|none
await client.startScreenShare({ selfOverlay: true }); // share screen with a self-cam bubble
// console.log(client.isHandRaised, client.e2eeActive);

// ── Verifiable peer identity (public-key) ──────────────────────────────────────
// Each participant announces an ECDH public key automatically. WebRTC already uses
// dynamic ephemeral ports + per-connection DTLS, so this adds a STABLE, verifiable
// identity on top: compare the safety number out-of-band to rule out a relay MITM.
// console.log(client.identityPublicKey);
// const safety = await client.getPeerSafetyNumber('callee-id'); // 60-digit code, both sides match

// ── Device / network attestation ───────────────────────────────────────────────
// event.context.location    -> consented GPS coordinates for map placement
// event.context.network     -> public IP, ISP, country/state/city, proxy/VPN flags
// event.context.attestation -> live provider, ICE path, TURN status, and E2EE verdict
// Browser-only WebRTC cannot prove "no proxy/VPN" without a trusted resolver or native feed.

// ── 1:1 WhatsApp-style calling (instead of join) ───────────────────────────────
// await client.start({ to: 'callee-id', roomId: '${roomId}', silent: false }); // silent:true = no ringtone

// ── Schedule a group call & share a calendar invite ────────────────────────────
// const call = client.schedule({ title: 'Standup', startsAt: Date.now() + 3600_000, invitees: ['amaka','tunde'] });
// const icsInvite = client.scheduledCallICS(call, 'https://app/call/' + call.roomId);
// later: await client.joinScheduled(call);`;
};

const selectPlatform = (platform) => {
  document.querySelectorAll('.platform-tab').forEach(b => b.classList.remove('active'));
  const activeBtn = document.querySelector(`.platform-tab[data-platform="${platform}"]`);
  if (activeBtn) activeBtn.classList.add('active');
  codeOutput.innerText = platform === 'web' ? buildWebSnippet() : (SNIPPETS[platform] || '');
};

document.querySelectorAll('.platform-tab').forEach(btn => {
  btn.onclick = () => {
    selectPlatform(btn.getAttribute('data-platform'));
  };
});

// Default snippet selection on load
selectPlatform('web');

// Keep the live web snippet in sync as the developer changes the mode / participant cap.
const refreshWebSnippetIfActive = () => {
  const active = document.querySelector('.platform-tab.active')?.getAttribute('data-platform');
  if (active === 'web') codeOutput.innerText = buildWebSnippet();
};
// Refresh the codegen whenever the Call Experience changes (it feeds the `ui` config + mountCallUI).
document.getElementById('selectCallUx')?.addEventListener('change', refreshWebSnippetIfActive);

const fieldsToPersist = [
  'inputRoomId',
  'inputRoomPassword',
  'inputSelfId',
  'inputRemoteId',
  'selectSignalingType',
  'selectBillingMode',
  'selectPeerRole',
  'selectCallUx',
  'selectMaxParticipants',
  'inputMediaKitUrl',
  'inputLivekitUrl',
  'inputWhipEndpoint',
  'inputWhepEndpoint',
  'inputWebhookUrl',
  'inputWebhookSecret',
  'inputAuthBaseUrl',
  'inputPublicAppKey',
  'inputRegionCode'
];

const saveConfig = () => {
  fieldsToPersist.forEach(id => {
    const el = document.getElementById(id);
    if (el) {
      localStorage.setItem(`config-${id}`, el.value);
    }
  });
};

const readResponseErrorDetail = async (res) => {
  try {
    const payload = await res.json();
    if (payload?.message && payload?.error && payload.error !== 'Internal Server Error') {
      return `${payload.error} (${payload.message})`;
    }
    return payload?.message || payload?.error || '';
  } catch {
    try {
      return (await res.text()).trim();
    } catch {
      return '';
    }
  }
};

const loadConfig = async () => {
  let loadedFromServer = false;

  // 1. Try to load shared parameters from server first
  try {
    const res = await fetch('/api/signaling/session-config', {
      headers: withBearer(getSessionToken())
    });
    if (res.status === 401) {
      redirectToLogin('auto');
      return;
    }
    if (res.ok) {
      const serverConfig = await res.json();
      if (serverConfig && Object.keys(serverConfig).length > 0) {
        consoleLog('Loaded shared launch settings from the signaling store.', 'success');
        Object.entries(serverConfig).forEach(([id, val]) => {
          // Exclude client-specific peer/participant IDs from global server overwrite
          if (id === 'inputSelfId' || id === 'inputRemoteId') {
            return;
          }
          const el = document.getElementById(id);
          if (el) {
            el.value = val;
            localStorage.setItem(`config-${id}`, val);
          }
        });
        loadedFromServer = true;
      }
    }
  } catch (e) {
    console.warn('Failed to load config from server:', e);
  }

  // 2. Load client-specific/local values, and shared ones only if they weren't loaded from server
  fieldsToPersist.forEach(id => {
    const isClientSpecific = (id === 'inputSelfId' || id === 'inputRemoteId');
    if (isClientSpecific || !loadedFromServer) {
      const el = document.getElementById(id);
      if (el) {
        const saved = localStorage.getItem(`config-${id}`);
        if (saved !== null && saved !== '') {
          el.value = saved;
        }
      }
    }
  });

  // 3. Guarantee a unique local selfId if empty (important for multi-session/multi-tab P2P testing)
  const selfIdEl = document.getElementById('inputSelfId');
  if (selfIdEl && !selfIdEl.value.trim()) {
    const randomSuffix = Math.floor(100 + Math.random() * 900);
    selfIdEl.value = `operator-${randomSuffix}`;
    localStorage.setItem('config-inputSelfId', selfIdEl.value);
  }

  syncBillingModeUI();
  syncProviderModeUI();
  syncExperienceUI();
  refreshWebSnippetIfActive();
};

const saveConfigToServer = async () => {
  const config = {};
  fieldsToPersist.forEach(id => {
    const el = document.getElementById(id);
    if (el) {
      config[id] = el.value;
    }
  });

  try {
    const res = await fetch('/api/signaling/session-config', {
      method: 'POST',
      headers: withBearer(getSessionToken(), {
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify(config)
    });
    if (res.status === 401) {
      redirectToLogin('auto');
      return;
    }
    if (res.ok) {
      consoleLog('💾 Persisted settings into the shared signaling store.', 'success');
      void initClientIfIdle().catch((err) => {
        console.warn('Failed to re-initialize RTC client after saving config:', err);
      });
    } else {
      const detail = await readResponseErrorDetail(res);
      consoleLog(`Failed to save settings: ${detail || res.statusText || `HTTP ${res.status}`}`, 'error');
    }
  } catch (e) {
    consoleLog(`Failed to save settings: ${e?.message || String(e)}`, 'error');
  }
};

// Listeners to save config on change
fieldsToPersist.forEach((id) => {
  document.getElementById(id)?.addEventListener('input', () => {
    saveConfig();
    syncExperienceUI();
    refreshWebSnippetIfActive();
  });
  document.getElementById(id)?.addEventListener('change', () => {
    saveConfig();
    syncExperienceUI();
    refreshWebSnippetIfActive();
  });
});

const selfIdField = document.getElementById('inputSelfId');
const invalidateRtcPeerIdentity = () => {
  clearRtcPeerToken();
  lastInitParams = null;
  lastRtcPeerToken = null;
};
selfIdField?.addEventListener('input', invalidateRtcPeerIdentity);
selfIdField?.addEventListener('change', invalidateRtcPeerIdentity);

// Save to server action button
const btnPersist = document.getElementById('btnPersistServerConfig');
if (btnPersist) {
  btnPersist.onclick = () => {
    saveConfig();
    saveConfigToServer();
  };
}

document.getElementById('btnCopySnippet').onclick = () => {
  navigator.clipboard.writeText(codeOutput.innerText);
  consoleLog('Copied integration snippet to clipboard.', 'success');
};

// Header Logout action
document.getElementById('btnHeaderLogout').onclick = async () => {
  const token = getSessionToken();
  try {
    await fetch('/api/auth/logout', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token || ''}`
      }
    });
  } catch (e) {
    // Ignore serverless reachability errors during logout
  }

  clearOperatorAuthState();
  window.location.replace('./login.html?logout=true');
};

// --- Online Presence & Peer Discovery Registry ---
let presenceInterval = null;
let localTurnStatus = 'unknown';
let localTurnProbe = null;

const expiryMs = (data) => {
  if (data?.expires) {
    const ts = Date.parse(data.expires);
    if (!Number.isNaN(ts)) return ts;
  }
  if (typeof data?.ttl === 'number') return Date.now() + data.ttl * 1000;
  return undefined;
};

const pingTurnServer = async (turnApiBase, turnToken, turnUsername, peerToken) => {
  const res = await fetch('/api/signaling/turn-ping', {
    method: 'POST',
    headers: withBearer(peerToken, { 'Content-Type': 'application/json' }),
    body: JSON.stringify({ turnApiBase, turnToken, turnUsername })
  });
  if (!res.ok) throw new Error('Proxy ping failed');
  return await res.json();
};

// Raw link capacity (fast.com-style probe). Distinct from media throughput; surfaced in the HUD.
let lastLinkCapacity = null;
const renderLinkCapacity = () => {
  // Re-render the attestation panel so the raw-capacity line refreshes in real time.
  if (window.__lastDeviceContext) updateHudDeviceContext(window.__lastDeviceContext);
};

// CoTURN SERVICE health (cert / redis / mongo / realm) from /v1/health, proxied server-side.
// Stored so the Session Attestation HUD can show real infra health, not just ping reachability.
let turnServiceHealth = null;
const fetchTurnServiceHealth = async (peerToken) => {
  try {
    const res = await fetch('/api/signaling/turn-health', {
      method: 'POST',
      headers: withBearer(peerToken, { 'Content-Type': 'application/json' }),
      body: JSON.stringify({}),
    });
    if (res.ok) turnServiceHealth = await res.json();
  } catch { /* best effort */ }
};

const sanitizeAuthBaseUrl = (url) => {
  if (!url) return '';
  let cleaned = url.trim();
  cleaned = cleaned.replace(/\/+$/, '');
  const indicators = ['/api/authsvc', '/v1/region', '/v1/credentials'];
  for (const indicator of indicators) {
    const idx = cleaned.toLowerCase().indexOf(indicator);
    if (idx !== -1) {
      cleaned = cleaned.substring(0, idx);
      break;
    }
  }
  return cleaned;
};

const triggerPresenceUpdate = async () => {
  const selfIdInput = document.getElementById('inputSelfId');
  if (!selfIdInput) return;
  const selfId = selfIdInput.value.trim();
  if (!selfId) return;

  try {
    const rtcPeerToken = await ensureRtcPeerToken(selfId);
    const res = await fetch('/api/signaling/presence', {
      method: 'POST',
      headers: withBearer(rtcPeerToken, { 'Content-Type': 'application/json' }),
      body: JSON.stringify({})
    });

    if (res.ok) {
      const data = await res.json();
      updatePresenceList(data.peers || [], selfId);
    } else if (res.status === 401) {
      redirectToLogin('auto');
    }
  } catch (e) {
    console.warn('[Presence] Failed to trigger presence update:', e);
  }
};

const checkLocalTurnLiveness = async () => {
  const oldStatus = localTurnStatus;

  // If the client is active and has established a connection, read status from client liveness event history
  if (client && client.turnLiveness) {
    localTurnStatus = client.turnLiveness.alive && client.turnLiveness.turnReachable ? 'online' : 'offline';
    if (localTurnStatus !== oldStatus) {
      triggerPresenceUpdate().catch(() => {});
    }
    return;
  }

  const rawAuthBaseUrl = document.getElementById('inputAuthBaseUrl').value.trim();
  const authBaseUrl = sanitizeAuthBaseUrl(rawAuthBaseUrl);
  const regionCode = document.getElementById('inputRegionCode').value.trim() || 'NG';
  const publicAppKey = document.getElementById('inputPublicAppKey').value.trim();
  const selfId = document.getElementById('inputSelfId').value.trim() || 'test-user';

  if (!authBaseUrl || !publicAppKey) {
    localTurnStatus = 'unknown';
    if (localTurnStatus !== oldStatus) {
      triggerPresenceUpdate().catch(() => {});
    }
    return;
  }

  try {
    const rtcPeerToken = await ensureRtcPeerToken(selfId);
    const mustRefreshProbe =
      !localTurnProbe ||
      localTurnProbe.peerId !== selfId ||
      !localTurnProbe.turnApiBase ||
      !localTurnProbe.turnToken ||
      !localTurnProbe.turnUsername ||
      (typeof localTurnProbe.expiresAt === 'number' && localTurnProbe.expiresAt - Date.now() <= 90_000);

    if (mustRefreshProbe) {
      const res = await fetch('/api/signaling/turn-credentials', {
        method: 'POST',
        headers: withBearer(rtcPeerToken, { 'Content-Type': 'application/json' }),
        body: JSON.stringify({ authBaseUrl, publicAppKey, regionCode })
      });
      if (!res.ok) throw new Error(`Proxy returned status ${res.status}`);
      const credsData = await res.json();

      localTurnProbe = {
        peerId: selfId,
        turnApiBase: credsData.turnApiBase || 'https://turns-stg.soa.africa',
        turnToken: credsData.turnToken,
        turnUsername: credsData.username,
        expiresAt: expiryMs(credsData)
      };
    }

    const pingData = await pingTurnServer(
      localTurnProbe.turnApiBase,
      localTurnProbe.turnToken,
      localTurnProbe.turnUsername,
      rtcPeerToken
    );
    localTurnStatus = pingData.alive && pingData.turn_reachable ? 'online' : 'unreachable';
    // Also pull the CoTURN SERVICE health (cert/redis/mongo/realm) for the attestation HUD.
    fetchTurnServiceHealth(rtcPeerToken).catch(() => {});
  } catch (e) {
    console.warn('[TURN Monitor] Failed to verify TURN liveness through proxy:', e.message || e);
    localTurnProbe = null;
    localTurnStatus = 'offline';
  }

  if (localTurnStatus !== oldStatus) {
    triggerPresenceUpdate().catch(() => {});
  }
};

function startPresencePolling() {
  if (presenceInterval) clearInterval(presenceInterval);

  const poll = async () => {
    const selfIdInput = document.getElementById('inputSelfId');
    if (!selfIdInput) return;
    const selfId = selfIdInput.value.trim();
    // A missing selfId is a config issue, not "no peers" — surface it instead of silence.
    if (!selfId) { renderPresenceError('Set a Participant ID to appear on the matchmaking network.'); return; }

    // Run active/idle TURN connectivity monitor tick in background (non-blocking)
    checkLocalTurnLiveness().catch(err => console.warn('[TURN Monitor] check failed:', err));

    try {
      const rtcPeerToken = await ensureRtcPeerToken(selfId);
      const res = await fetch('/api/signaling/presence', {
        method: 'POST',
        headers: withBearer(rtcPeerToken, { 'Content-Type': 'application/json' }),
        body: JSON.stringify({})
      });

      if (res.ok) {
        const data = await res.json();
        updatePresenceList(data.peers || [], selfId);
        // Feed the SDK's native Online Presence Directory (enriches with e2ee/turns, emits
        // 'presence-changed'). Turns the presence poll into a first-class SDK signal.
        try {
          client?.ingestPresence?.((data.peers || []).map(p => ({
            peerId: p.peerId,
            online_status: 'online',
            turns_status: p.turnStatus === 'online' ? 'online' : p.turnStatus === 'unreachable' ? 'unreachable' : 'offline',
            roomId: p.roomId,
          })));
        } catch { /* directory feed best-effort */ }
      } else {
        // Don't silently show "no peers" — a 5xx (e.g. ADMIN_SECRET unset → signaling 500) or
        // 401 here is why discovery looks empty. Make the real cause visible.
        let detail = '';
        try {
          detail = await readResponseErrorDetail(res);
        } catch {}
        const hint = res.status >= 500
          ? 'Signaling backend error — check the shared signaling store configuration for the functions.'
          : res.status === 401 ? 'Unauthorized — sign in again to refresh your operator session.'
          : `HTTP ${res.status}`;
        renderPresenceError(`Presence unavailable: ${hint}${detail ? ` (${detail})` : ''}`);
        console.warn('[Presence] endpoint returned', res.status, detail);
        if (res.status === 401) {
          redirectToLogin('auto');
        }
      }
    } catch (e) {
      renderPresenceError('Presence endpoint unreachable — is the signaling dev server running?');
      console.warn('[Presence] Failed to register presence / fetch peers:', e);
    }
  };

  poll();
  presenceInterval = setInterval(poll, 5000);
}

// Distinct error state for the directory, so a backend/config failure never masquerades
// as "no peers online" (the symptom that hides auth/signaling problems).
function renderPresenceError(message) {
  const el = document.getElementById('presencePeerList');
  if (!el) return;
  el.innerHTML = `<div class="presence-empty">⚠️ ${message}</div>`;
}

function updatePresenceList(peers, selfId) {
  const presencePeerList = document.getElementById('presencePeerList');
  if (!presencePeerList) return;

  // Filter out ourselves
  const otherPeers = peers.filter(p => p.peerId !== selfId);

  if (otherPeers.length === 0) {
    presencePeerList.innerHTML = `
      <div class="presence-empty">
        No other online peers detected. Open another browser tab to test discovery.
      </div>
    `;
    return;
  }

  // Enrich rows from the SDK's native Online Presence Directory (e2ee_status, and — for
  // Connectra+ — peerRole + availability_status), keyed by peerId.
  const dir = new Map((client?.onlinePeers || []).map(p => [p.peerId, p]));

  let html = '';
  otherPeers.forEach(peer => {
    const inRoom = !!peer.roomId;
    const sdkPeer = dir.get(peer.peerId) || {};
    const statusText = inRoom ? 'Busy (In Session)' : 'Available';
    const statusState = inRoom ? 'warn' : 'good';
    const pid = escapeHtml(peer.peerId);
    const rid = escapeHtml(peer.roomId);
    const actionButtons = [];
    if (inRoom) {
      actionButtons.push(`<button class="btn-probe" type="button" data-presence-action="join" data-peer-id="${pid}" data-room-id="${rid}">Join</button>`);
    }
    actionButtons.push(`<button class="btn-accent" type="button" data-presence-action="call" data-peer-id="${pid}">Call</button>`);
    const actionHtml = `<div class="presence-actions">${actionButtons.join('')}</div>`;

    const turnState = peer.turnStatus === 'online' ? 'good' : peer.turnStatus === 'unreachable' ? 'warn' : 'bad';
    const turnText = peer.turnStatus === 'online' ? 'TURN Connected' : peer.turnStatus === 'unreachable' ? 'TURN Unstable' : 'TURN Offline';

    // e2ee_status pill (from the SDK directory): active = ciphertext-only P2P confirmed.
    const e2ee = sdkPeer.e2ee_status || 'unknown';
    const e2eeState = e2ee === 'active' ? 'good' : e2ee === 'unsupported' ? 'bad' : 'warn';
    const e2eeText = e2ee === 'active' ? 'E2EE Active' : e2ee === 'unsupported' ? 'E2EE Off' : 'E2EE Pending';

    // Connectra+ only: surface peer role + operator availability.
    let rolePill = '';
    if (sdkPeer.peerRole) {
      const av = sdkPeer.availability_status;
      const avState = av === 'active' ? 'good' : av === 'busy' ? 'warn' : 'bad';
      const roleLabel = sdkPeer.peerRole.charAt(0).toUpperCase() + sdkPeer.peerRole.slice(1);
      rolePill = `<span class="presence-pill" data-state="${av ? avState : 'good'}">
              <span class="dot"></span>
              ${roleLabel}${av ? ` · ${av}` : ''}
            </span>`;
    }

    html += `
      <div class="presence-row">
        <div class="presence-main">
          <span class="presence-id">${pid}</span>
          <div class="presence-meta">
            <span class="presence-pill" data-state="${statusState}">
              <span class="dot"></span>
              ${statusText}
            </span>
            ${rolePill}
            <span class="presence-pill" data-state="${turnState}">
              <span class="dot"></span>
              ${turnText}
            </span>
            <span class="presence-pill" data-state="${e2eeState}">
              <span class="dot"></span>
              ${e2eeText}
            </span>
          </div>
        </div>
        ${actionHtml}
      </div>
    `;
  });

  presencePeerList.innerHTML = html;
}

// Global action handlers triggered by the Online Presence list
window.handlePresenceJoin = (peerId, roomId) => {
  document.getElementById('inputRemoteId').value = peerId;
  document.getElementById('inputRoomId').value = roomId;
  document.getElementById('btnJoinRoom').click();
};

window.handlePresenceCall = (peerId) => {
  document.getElementById('inputRemoteId').value = peerId;
  document.getElementById('btnStartCall').click();
};

// Single delegated listener for the presence list (rows are rebuilt each poll, so per-row
// listeners would churn). Peer ids/rooms travel as data-* attributes, never as inline JS.
document.getElementById('presencePeerList')?.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-presence-action]');
  if (!btn) return;
  if (btn.dataset.presenceAction === 'join') {
    window.handlePresenceJoin(btn.dataset.peerId, btn.dataset.roomId);
  } else if (btn.dataset.presenceAction === 'call') {
    window.handlePresenceCall(btn.dataset.peerId);
  }
});

// Load configuration and start polling sequentially on load
(async () => {
  await loadConfig();
  await initClientIfIdle();
  syncExperienceUI();
  startWanPingLoop();
  startPresencePolling();
})();

window.addEventListener('beforeunload', () => {
  stopWanPingLoop();
});
